import { CONFIG } from './config';
import { Pool, clamp, lerp, sampleN, TAU, dist2, angleTo, rand, pick, weightedPick, chance, weaponDef } from './utils';
import { Player, makeEnemy, makeProj, makePickup, makeParticle, makeDmgNum } from './entities';
import { Meta } from './meta';
import { Spawner } from './spawner';
import { Weapons } from './weapons';
import { Input } from './input';
import { Audio2 } from './audio';
import { t, tName, loreList } from './locale';

/* =====================================================================
   GAME — состояние, апдейт, рендер, коллизии. Центр всего.
   Состояния: menu | charselect | shop | playing | levelup | shrine | paused | gameover | win | settings
   ===================================================================== */

export const Game: any = {
  state: 'menu',
  player: null,
  enemies: null, projectiles: null, pickups: null, particles: null, dmgNumbers: null,
  effects: [],
  time: 0,
  score: 0,
  viewW: 960, viewH: 600,
  camera: { x: 0, y: 0 },
  shake: 0, shakeX: 0, shakeY: 0,
  bannerText: '', bannerTime: 0, bannerMax: 1,
  pendingLevels: 0,
  offers: [],
  selIndex: 0,
  bossActive: false,
  started: false,
  selectedChar: 'spark',
  charIndex: 0,
  shopIndex: 0,
  runGold: 0,
  // ---- расширение v2: глубины, аномалии, зоны тьмы, события ----
  selectedDepth: 0,         // выбранная глубина (0 = базовый забег)
  selectedPath: 0,          // выбранный Путь (стартовый архетип; 0 = Странник, ванильный старт)
  depthIndex: 0,            // фактическая глубина забега
  runMods: null,            // слитые модификаторы забега (глубина × аномалии)
  darkZones: [],            // пятна тьмы от Якорей: {x,y,r,life,maxLife}
  emberField: [],           // выжженная земля (память формы): след тёплых меток {x,y,born}
  _emberLast: null,         // последняя позиция эмиссии метки следа
  moveTarget: null,         // click-to-move: мировая точка-цель (клик мышью), null = нет
  eventsSpawned: 0,         // сколько Развилок Тьмы уже было
  _lastEventT: 0,           // время последней развилки (якорь периодических развилок вечной ночи)
  runSeen: null,            // Set typeKey врагов, встреченных в забеге (кодекс)
  killedBoss: false,        // убит ли хоть один босс (веха разблокировки)
  newUnlocks: [],           // что открылось этим забегом (для итогового экрана)
  // ---- бесконечность: Рассвет (веха) + вечная ночь ----
  reachedDawn: false,       // достигнут ли Рассвет (10:00) — засчитывает мету, право уйти победой
  bossKills: 0,             // сколько боссов убито (для эскалации поздних боссов)
  // ---- точки интереса: Святилища в пространстве ----
  shrines: [],              // активные святилища: {x,y,kind,def,pulse}
  shrineTimer: 0,           // таймер спавна следующего святилища
  relicsOwned: [],          // ключи взятых реликвий (для итогов/иконок)
  goldMult: 1,              // множитель золота с врагов (реликвия Алчность)
  bless: null,              // временный баф от Источника: {dmg,life,maxLife}
  // кликабельные UI-зоны (выставляются при рендере соответствующего экрана)
  _menuShopRect: null, _depthRects: null, _pauseRects: null, _volRect: null,
  _resultMenuRect: null, _shopBackRect: null,

  init() {
    this.enemies = new Pool(makeEnemy);
    this.projectiles = new Pool(makeProj);
    this.pickups = new Pool(makePickup);
    this.particles = new Pool(makeParticle);
    this.dmgNumbers = new Pool(makeDmgNum);
    this.player = new Player();
    // «возврат» — реоткрытие игры после долгого перерыва (§14.4): голос Пепелища на меню
    this.menuVoice = '';
    if (typeof Meta !== 'undefined' && Meta.data.lastRunAt && (Date.now() - Meta.data.lastRunAt) > 24 * 3600 * 1000) {
      this.menuVoice = this._pickLore('menuReturn');
    }
  },

  // между-забеговый фрагмент для меню по итогу последнего забега (§14.4)
  _menuFragment() {
    const md = (typeof Meta !== 'undefined') ? Meta.data : null;
    if (!md) return '';
    if (md.lastRunWon) return '';                               // победа звучит на экране победы — меню не дублирует
    if ((md.noWinStreak || 0) >= 3) return this._pickLore('menuStreak');
    if ((md.lastRunTime || 0) < 60) return this._pickLore('menuEarly');
    if ((md.lastRunTime || 0) >= 360) return this._pickLore('menuDeep');
    return this._pickLore('menuDefault');
  },

  start() {
    const char = CONFIG.characters[this.selectedChar] ? this.selectedChar : 'spark';
    // глубина: не выше открытой (в демо-сборке — capped до demo.maxDepth)
    const maxD = (typeof Meta !== 'undefined') ? Meta.maxDepthAllowed() : 0;
    this.depthIndex = clamp(this.selectedDepth, 0, maxD);
    // Путь: валидируем выбор (диапазон + разблокирован; иначе Странник). Завет Пути вливается ВНУТРИ computeRunMods.
    this.selectedPath = clamp(this.selectedPath || 0, 0, CONFIG.paths.length - 1);
    const pathDef = CONFIG.paths[this.selectedPath];
    if (typeof Meta !== 'undefined' && pathDef && !Meta.isUnlocked(pathDef.key)) this.selectedPath = 0;
    this.computeRunMods();
    this.player.reset(char);
    this.player.addWeapon(CONFIG.characters[char].start);   // стартовое оружие персонажа
    this.player.recalc();
    this.enemies.clear(); this.projectiles.clear(); this.pickups.clear();
    this.particles.clear(); this.dmgNumbers.clear();
    this.effects.length = 0;
    this.darkZones.length = 0;
    this.time = 0; this.score = 0;
    this.shake = 0; this.bannerTime = 0;
    this.pendingLevels = 0; this.offers = [];
    // пересборы драфта за забег: база 1 + «Лишний уголь» (мета-Горнило). Агентность, не сила.
    this.rerollsLeft = 1 + ((typeof Meta !== 'undefined') ? Meta.upgLevel('reroll') : 0);
    // частота святилищ: «Память святилищ» (мета-Горнило) — горят чаще (исследование, не сила)
    this.shrineEveryMul = 1 / (1 + 0.3 * ((typeof Meta !== 'undefined') ? Meta.upgLevel('beacons') : 0));
    this.bossActive = false;
    this.eventsSpawned = 0;
    this._lastEventT = 0;   // якорь периодических развилок вечной ночи (сброс между забегами)
    this.killedBoss = false;
    this.runSeen = new Set();
    this.newUnlocks = [];
    this._runAwarded = false;
    this.reachedDawn = false;
    this.bossKills = 0;
    this.shrines.length = 0;
    this.shrineTimer = CONFIG.shrines.spawnEvery * 0.6 * this.shrineEveryMul;   // первое святилище — раньше обычного
    this.relicsOwned.length = 0;
    this.goldMult = 1;
    this.bless = null;
    this.demoEnd = false;   // демо-хук на выходе (см. demoComplete)
    this._lureT = 0; this._lureCd = 0;   // ПРИМАНКА: время непрерывного гашения + кулдаун после burst
    this.snareT = 0; this.snareBoon = null; this.snareBoonDef = null;   // TD-захват: активное окно удержания
    this.snareX = 0; this.snareY = 0; this.snarePulseT = 0; this.snareLightBonus = 0;
    this.snareKindle = 0;   // бар розжига зоны (0→1): растёт в зоне, спадает вне; kindle=1 → ранняя вспышка-успех
    this._snareSpawnX = 0; this._snareSpawnY = 0;   // якорь зоны (координаты точки), кэш между triggerShrine→beginSnare
    this.snareMode = 'kindle';   // архетип активной точки: 'kindle' (Разжечь, слой A) | 'defend' (Оборона, слой B)
    this.snareCoreHp = 0; this.snareCoreMaxHp = 0;  // целостность ядра-очага (defend) — 0 при провале → endSnare(false)
    this._snareLastMode = null; this._snareCount = 0;   // селектор архетипа: первая всегда kindle, дальше без повтора
    this.embers = []; this.emberBudget = 0; this._emberQueue = [];   // ПРОТОТИП свет-маятника: угли-турели из билда в обороне
    this.snareWave = 0; this.snareWavePhase = 'fighting'; this.snareWaveT = 0;   // слой 1: многоволновая TD-стадия обороны
    this.runName = this._pickLore('runNames');   // имя забега — микро-нарратив (§14.2)
    this.deathVoice = ''; this.winVoice = ''; this.draftVoice = '';
    this.speak = null;
    this.ashMarks = [];
    this.emberField = []; this._emberLast = null;
    this.moveTarget = null;
    // ПУТЬ: стартовый кит через СУЩЕСТВУЮЩУЮ машинерию (оружие/пассивка/реликвия) — ПОСЛЕ сброса
    // run-стейта (relicsOwned/goldMult чисты, иначе applyRelic пропустит реликвию). Не новые поля.
    const path = CONFIG.paths[this.selectedPath];
    if (path) {
      if (path.startWeapon && !this.player.hasWeapon(path.startWeapon)) this.player.addWeapon(path.startWeapon);
      if (path.startPassive) this.player.addPassive(path.startPassive);
      if (path.startRelic) this.applyRelic(path.startRelic);
      this.player.recalc();
    }
    Spawner.reset();
    this.started = true;
    // Аномалии: либо игрок выбирает их («Завет тьмы»), либо тихий ролл (старый режим).
    // Форс-аномалия глубины уже влита в computeRunMods — добираем только остаток.
    this._anomalyPicksLeft = this.anomalyPicksRemaining();
    // ОНБОРДИНГ (OGD): «Завет тьмы» — раскрывается по прогрессии (Качественный Порог). До раскрытия —
    // ЧИСТЫЙ забег: без драфта заветов и без тихих роллов (Нулевой порог Тренажёра — первая схема ядра).
    const anomRevealed = (typeof Meta === 'undefined') || Meta.isUnlocked('anomalies');
    if (!anomRevealed) {
      this.beginPlay();
    } else if (CONFIG.chooseAnomalies) {
      this.openAnomalyDraft();   // экран выбора; в игру войдём после последнего выбора (beginPlay)
    } else {
      this.applyRolledAnomalies();
      this.beginPlay();
    }
  },

  // случайный лор-фрагмент «Пепелище» (§14). Math.random намеренно — чисто косметика,
  // НЕ трогает игровой RNG (детерминизм симуляции/harness не возмущается).
  _pickLore(cat) {
    const l = loreList(cat);
    return l.length ? l[Math.floor(Math.random() * l.length)] : '';
  },

  // фактический вход в забег (после возможного экрана выбора аномалий)
  beginPlay() {
    this._draftIsAnomaly = false;
    this.offers = [];
    this.state = 'playing';
    // билд-ось Границы: аномалия-завет могла дать бонус урона по свету — влить в игрока
    const pp = this.player;
    if (this.runMods.loEdge) pp._loEdge = (pp._loEdge || 0) + this.runMods.loEdge;
    if (this.runMods.hiEdge) pp._hiEdge = (pp._hiEdge || 0) + this.runMods.hiEdge;
    // сбросить зажатие мыши с кнопки старта/выбора — иначе игрок сразу едет к курсору
    if (typeof Input !== 'undefined') Input.mouseDown = false;
    Audio2.ensure(); Audio2.resume(); Audio2.startMusic();
    Audio2.musicSetMood(this.depthIndex >= 3 ? 'deep' : 'night');
  },

  // ---- «Завет тьмы»: игрок выбирает, какой аномалией обернётся ночь ----
  // Переиспользует UI/ввод драфта (state='levelup' + флаг _draftIsAnomaly). Выбор
  // повторяется anomalyCount() раз — каждый раз свежий набор из не взятых аномалий.
  openAnomalyDraft() {
    const have = new Set(this.runMods.anomalies.map((a: any) => a.key));
    const pool = CONFIG.anomalies.filter((a: any) => !have.has(a.key));
    if (!pool.length || this._anomalyPicksLeft <= 0) { this.beginPlay(); return; }
    this.offers = sampleN(pool, Math.min(3, pool.length)).map((a: any) => ({ type: 'anomaly', def: a }));
    this.selIndex = 0;
    this._draftIsAnomaly = true;
    this._draftIsEvent = false;
    this._draftIsShrine = false;
    this.state = 'levelup';
    this._draftOpenClock = this.clock;
    this.dmgNumbers.clear();
    Audio2.levelup();
  },

  computeRunMods() {
    const m: any = { depth: this.depthIndex, anomalies: [],
                hp: 1, spd: 1, dmg: 1, light: 1, reward: 1,
                recover: 1, hitLoss: 1, darkSpeed: 1, pickup: 1, xp: 1, weightMul: {},
                loEdge: 0, hiEdge: 0 };   // билд-ось Границы: бонус урона по свету от аномалий-заветов
    // глубина: множители + её тематическая аномалия (характер места, не только числа)
    if (this.depthIndex > 0) {
      const d = CONFIG.depths[this.depthIndex - 1];
      m.hp *= d.hp; m.spd *= d.spd; m.dmg *= d.dmg; m.light *= d.light; m.reward *= d.reward;
      if (d.forceAnomaly) {
        const fa = CONFIG.anomalies.find((a: any) => a.key === d.forceAnomaly);
        if (fa) { this._mergeAnomaly(m, fa); m.anomalies.push(fa); }
      }
    }
    // ПУТЬ: стартовый завет вливается как форс-аномалия (тот же механизм, что depth.forceAnomaly),
    // ИМЕННО здесь — чтобы попал в набор и учёлся в anomalyPicksRemaining (иначе игрок получит лишний пик).
    const path = CONFIG.paths[this.selectedPath || 0];
    if (path && path.startCovenant) {
      const fa = CONFIG.anomalies.find((a: any) => a.key === path.startCovenant);
      if (fa && !m.anomalies.some((a: any) => a.key === fa.key)) { this._mergeAnomaly(m, fa); m.anomalies.push(fa); }
    }
    // ОСТАЛЬНЫЕ аномалии вливаются отдельно: выбором игрока (openAnomalyDraft) либо
    // роллом (applyRolledAnomalies) — см. start(). Форс-аномалия глубины уже в наборе.
    this.runMods = m;
  },

  // сколько аномалий всего в забеге: 1 на поверхности, +1 за каждые две глубины (вкл. форс глубины)
  anomalyCount() {
    // Кузня аномалий (мета): +N выборов Завета тьмы на старте (горизонталь — аномалии это risk/reward,
    // не сырая сила; пик занимает слот, кап поднимается ровно на купленное, не сверх).
    const forge = (typeof Meta !== 'undefined') ? Meta.upgLevel('anoma') : 0;
    return clamp(1 + Math.floor(this.depthIndex / 2) + forge, 1, 3 + forge);
  },

  // сколько игроку ещё ДОБРАТЬ сверх уже навязанных глубиной форс-аномалий
  anomalyPicksRemaining() {
    return Math.max(0, this.anomalyCount() - this.runMods.anomalies.length);
  },

  // прежнее поведение (скрытый рандом) — когда выбор отключён в конфиге.
  // Доролл до anomalyCount() с учётом уже навязанной форс-аномалии глубины.
  applyRolledAnomalies() {
    const have = new Set(this.runMods.anomalies.map((a: any) => a.key));
    const pool = CONFIG.anomalies.filter((a: any) => !have.has(a.key));
    for (const a of sampleN(pool, Math.min(this.anomalyPicksRemaining(), pool.length))) {
      this._mergeAnomaly(this.runMods, a);
      this.runMods.anomalies.push(a);
    }
  },

  // влить модификаторы одной аномалии в набор (используется и при роллинге, и Развилкой)
  _mergeAnomaly(m, a) {
    if (a.light)    m.light    *= a.light;
    if (a.enemyHp)  m.hp       *= a.enemyHp;
    if (a.enemySpd) m.spd      *= a.enemySpd;
    if (a.enemyDmg) m.dmg      *= a.enemyDmg;
    if (a.reward)   m.reward   *= a.reward;
    if (a.recover)  m.recover  *= a.recover;
    if (a.hitLoss)  m.hitLoss  *= a.hitLoss;
    if (a.darkSpeed)m.darkSpeed*= a.darkSpeed;
    if (a.pickup)   m.pickup   *= a.pickup;
    if (a.xp)       m.xp       *= a.xp;
    if (a.loEdge)   m.loEdge   = (m.loEdge || 0) + a.loEdge;   // завет тьмы: урон растёт при низком жаре
    if (a.hiEdge)   m.hiEdge   = (m.hiEdge || 0) + a.hiEdge;
    if (a.weightMul) for (const k in a.weightMul) m.weightMul[k] = (m.weightMul[k] || 1) * a.weightMul[k];
  },

  // множитель «дыхания» света День/Ночь — частота нарастает к финалу
  dayNightMult() {
    const c = CONFIG.cycle;
    const p = lerp(c.period, c.periodEnd, clamp(this.time / CONFIG.dawnTime, 0, 1));
    return 1 + Math.sin(this.time / p * TAU) * c.swing;
  },

  // ----------------------------- UPDATE -----------------------------
  update(dt) {
    // «Говорящая фигура» у тайника/источника: мир заморожен, фигура говорит, авто-уход по таймеру.
    // Тикаем ЗДЕСЬ (а не только во frame-цикле), чтобы симуляция/harness не зависали на тайнике.
    if (this.state === 'speak') { this.updateSpeak(dt); return; }
    if (this.state !== 'playing') return;
    this.time += dt;
    Audio2.musicTick(dt);   // двигатель генеративной музыки (аккорды/мелодия)
    // Рассвет — веха (засчёт меты + право уйти победой), НО забег продолжается бесконечно
    if (this.time >= CONFIG.dawnTime && !this.reachedDawn) this.reachDawn();

    this.updatePlayer(dt);
    Spawner.update(dt, this.time);
    Weapons.update(this.player, dt);
    this.updateEnemies(dt);
    this.updateProjectiles(dt);
    this.updatePickups(dt);
    this.updateParticles(dt);
    this.updateDmgNumbers(dt);
    this.updateEffects(dt);
    this.updateDarkZones(dt);
    this.updateShrines(dt);
    if (this.snareT > 0) this.updateSnare(dt);   // TD-захват: таймер удержания + давление + опора
    this.updateEmbers(dt);                        // ПРОТОТИП свет-маятника: угли-турели стреляют из своих точек
    if (this.player._auraThorns) this.updateAura(dt);
    if (this.bless) { this.bless.life -= dt; if (this.bless.life <= 0) this.bless = null; }

    // shake decay
    if (this.shake > 0) this.shake = Math.max(0, this.shake - CONFIG.feel.shakeDecay * dt);
    if (this.bannerTime > 0) this.bannerTime -= dt;

    this.enemies.sweep(); this.projectiles.sweep(); this.pickups.sweep();
    this.particles.sweep(); this.dmgNumbers.sweep();

    // Развилка Тьмы: фикс. времена в раннем забеге, ЗАТЕМ периодически в вечной ночи
    // (после исчерпания eventTimes — каждые eventEvery сек, чтобы поток не уплощался после ~7 мин)
    if (this.state === 'playing') {
      const fixedDue = this.eventsSpawned < CONFIG.eventTimes.length && this.time >= CONFIG.eventTimes[this.eventsSpawned];
      const endlessDue = this.eventsSpawned >= CONFIG.eventTimes.length
        && (this.time - (this._lastEventT || 0)) >= CONFIG.eventEvery;
      // ОНБОРДИНГ: развилки Тьмы раскрываются по прогрессии (условие добавлено к существующему if,
      // не оборачивает блок — иначе срубило бы и левел-ап драфт ниже)
      const eventsRevealed = (typeof Meta === 'undefined') || Meta.isUnlocked('events');
      if ((fixedDue || endlessDue) && eventsRevealed) { this.openEvent(); return; }
    }
    // драфт по накопленным уровням
    if (this.pendingLevels > 0 && this.state === 'playing') this.openDraft();
  },

  updateDarkZones(dt) {
    const z = this.darkZones;
    for (let i = z.length - 1; i >= 0; i--) {
      z[i].life -= dt;
      if (z[i].life <= 0) z.splice(i, 1);
    }
  },

  // ---- Святилища: точки интереса в пространстве (мотивация исследовать) ----
  // Появляются вдали от игрока по таймеру. Подойти → награда. Святилище(relic) даёт
  // выбор реликвии (отдельный state='shrine', не конфликтует с драфтом уровней);
  // Тайник/Источник срабатывают мгновенно (золото / лечение + временный баф).
  updateShrines(dt) {
    // ОНБОРДИНГ: Святилища раскрываются по прогрессии (первая система-порог после забега 1)
    if (typeof Meta !== 'undefined' && !Meta.isUnlocked('shrines')) return;
    const S = CONFIG.shrines;
    const p = this.player;
    // спавн нового, если не достигнут лимит
    this.shrineTimer -= dt;
    if (this.shrineTimer <= 0) {
      const every = S.spawnEvery * (this.shrineEveryMul || 1);   // «Память святилищ» (мета): чаще
      if (this.shrines.length < S.maxActive) { this.shrineTimer += every; this.spawnShrine(); }
      else this.shrineTimer = every;   // лимит активных достигнут — не копим долг таймера
    }
    // активация при подходе
    const ar = S.radius + p.radius;
    for (let i = this.shrines.length - 1; i >= 0; i--) {
      const sh = this.shrines[i];
      if (dist2(sh.x, sh.y, p.x, p.y) <= ar * ar) {
        this.shrines.splice(i, 1);
        this.triggerShrine(sh);
        return;   // один на кадр (relic откроет экран выбора)
      }
    }
  },

  spawnShrine() {
    const S = CONFIG.shrines;
    const p = this.player;
    const weights: any = {};
    // Тенёта (TD-захват) — только с snare.minTime: обездвиживание требует собранного билда
    for (const k of S.kinds) {
      if (k.key === 'snare' && this.time < CONFIG.snare.minTime) continue;
      weights[k.key] = k.weight;
    }
    const key = weightedPick(weights);
    const def = S.kinds.find((k: any) => k.key === key);
    const a = rand(0, TAU);
    const r = rand(S.minDist, S.maxDist);
    this.shrines.push({ x: p.x + Math.cos(a) * r, y: p.y + Math.sin(a) * r,
                        kind: key, def, born: this.time });
  },

  triggerShrine(sh) {
    const p = this.player;
    let kind = sh.kind;
    // все реликвии собраны → Святилище ведёт себя как Источник (не открываем пустой экран)
    if (kind === 'relic' && Object.keys(CONFIG.relics).every(k => this.relicsOwned.includes(k))) kind = 'font';
    if (kind === 'relic') {
      // выбор реликвии — отдельный экран (переиспользует UI драфта, но свой state)
      this.offers = this.generateRelicOffers();
      this.selIndex = 0;
      this._draftIsEvent = false;
      this._draftIsShrine = true;
      this._draftIsAnomaly = false;
      this.draftVoice = this._pickLore('shrineRelic');   // голос реликвии (§14.3)
      this.state = 'shrine';
      this._draftOpenClock = this.clock;
      this.dmgNumbers.clear();
      this.bannerTime = 0;   // заголовок экрана и так есть — не дублируем застывшим баннером
      Audio2.levelup();
    } else if (kind === 'snare') {
      // ТЕНЁТА (TD-захват): выбор АРХЕТИПА точки → ВЫБОР опоры (драфт) → удержание/оборона.
      this._snareSpawnX = sh.x; this._snareSpawnY = sh.y;   // якорь = позиция ТОЧКИ (не игрока)
      this._snareCount = (this._snareCount || 0) + 1;
      this.snareMode = this._pickSnareMode();               // первая всегда kindle, дальше без повтора
      this._snareLastMode = this.snareMode;
      this.offers = CONFIG.snare.boons.map((b: any) => ({ type: 'boon', key: b.key, def: b }));
      this.selIndex = 0;
      this._draftIsEvent = false; this._draftIsShrine = true; this._draftIsAnomaly = false; this._draftIsSnare = true;
      this.draftVoice = '';
      this.state = 'shrine';
      this._draftOpenClock = this.clock;
      this.dmgNumbers.clear();
      this.bannerTime = 0;
      Audio2.boss();   // тяжёлый аккорд — точка схватила
    } else if (kind === 'cache' || kind === 'font') {
      // Тайник/Источник: вместо мгновенной вспышки — ГОВОРЯЩАЯ ФИГУРА (вес момента §12).
      // Мир замирает, пепельный призрак говорит фразу, эффект применяется при уходе.
      const voice = kind === 'cache' ? (this._pickLore('shrineCache') || t('banner.cache'))
                                     : (this._pickLore('shrineFont') || t('banner.font'));
      this.enterSpeak(kind, voice, sh.x, sh.y);
    }
  },

  // ВЫБОР АРХЕТИПА точки: первая Тенёта в забеге — всегда 'kindle' (мягкий онбординг перед вариантом-с-провалом),
  // дальше — случайно из archetypes БЕЗ повтора подряд. rand сидирован (детерминизм забега не ломается).
  _pickSnareMode() {
    const modes = CONFIG.snare.archetypes || ['kindle'];
    if ((this._snareCount || 0) <= 1) return 'kindle';
    const pool = modes.filter((m: string) => m !== this._snareLastMode);
    const src = pool.length ? pool : modes;
    return src[Math.floor(rand(0, src.length))];   // rand(0,N) ∈ [0,N) → индекс [0,N-1]
  },

  // НАЧАТЬ УДЕРЖАНИЕ: окно snare.dur (макс), игрок МОБИЛЕН, выбранная опора активна на окно.
  // kindle — держи зону (бар розжига); defend — защити ядро-очаг с целостностью от роя.
  beginSnare(boonKey) {
    const p = this.player, S = CONFIG.snare;
    const b = S.boons.find((x: any) => x.key === boonKey) || S.boons[0];
    this.snareBoon = boonKey; this.snareBoonDef = b;
    this.snareX = this._snareSpawnX; this.snareY = this._snareSpawnY;   // якорь = позиция ТОЧКИ
    this.snareKindle = 0;
    // ОБОРОНА: поднять ядро с целостностью (фикс HP на всех глубинах — инвариант «пассивность=провал»)
    if (this.snareMode === 'defend') {
      const D = S.defend;
      this.snareT = D.waves * D.waveDur + (D.waves - 1) * D.waveGap;   // общий таймер-страховка = вся длина стадии (~35с)
      this.snareCoreMaxHp = D.coreHp; this.snareCoreHp = this.snareCoreMaxHp;
      // слой 1: многоволновая стадия — первая волна стартует сразу
      this.snareWave = 1; this.snareWavePhase = 'fighting'; this.snareWaveT = D.waveDur;
      this.spawnSnareWave(1);
      // ПРОТОТИП свет-маятника: очередь углей = поддерживаемые оружия ТВОЕГО билда (билд читается в обороне)
      const kinds = D.emberKinds;
      this._emberQueue = p.weapons.filter((w: any) => kinds.includes(weaponDef(w.key).kind)).map((w: any) => ({ key: w.key, level: w.level }));
      if (!this._emberQueue.length) this._emberQueue = [{ key: 'bolt', level: 1 }];   // fallback: всегда есть что щупать
      this.embers = []; this.emberBudget = Math.min(D.emberMaxBudget, this._emberQueue.length);
    } else {
      this.snareT = S.dur;   // kindle — единое окно
      this.snareCoreHp = 0; this.snareCoreMaxHp = 0; this.embers = []; this.emberBudget = 0; this._emberQueue = [];
    }
    this.snarePulseT = 0; this.snareLightBonus = 0;
    // эффекты-опоры на окно захвата (читаются на use-site по _snare*; recalc их не трогает)
    p._snareDR = b.dr ? (1 - b.dr) : 1;
    p._snareCdMul = b.cdMul || 1;
    p._snareDmgMul = b.dmg || 1;
    // ПОЛЮС-ЧТЕНИЕ: тёмный держит ЗЛЕЕ (+урон), светлый — ШИРЕ (+радиус света на окно)
    if ((p._loEdge || 0) + (p._synLo || 0) > 0) p._snareDmgMul *= (1 + S.darkDmg);
    if ((p._hiEdge || 0) + (p._synHi || 0) > 0) this.snareLightBonus = S.lightWide;
    this.banner(t(this.snareMode === 'defend' ? 'banner.snareDefend' : 'banner.snare'), 1.8);
    this.addShake(12);
  },

  // ОБНОВЛЕНИЕ УДЕРЖАНИЯ: окно-таймер + capture-давление + (kindle: розжиг зоны | defend: урон ядру) + опора + исход.
  updateSnare(dt) {
    if (this.snareT <= 0) return;
    const p = this.player, S = CONFIG.snare, b = this.snareBoonDef;
    const defend = this.snareMode === 'defend';
    this.snareT -= dt;
    // capture-давление: стягивать рой к ТОЧКЕ (defend — СИЛЬНЕЕ, рой садится на ядро) + считать контест-врагов
    const pr2 = S.pullRadius * S.pullRadius, zr2 = S.zoneRadius * S.zoneRadius;
    const pullForce = defend ? S.defend.pullForce : S.pullForce;
    const chr2 = defend ? S.defend.coreHitRadius * S.defend.coreHitRadius : 0;
    let inZoneEnemies = 0, coreAttackers = 0;
    for (const e of this.enemies.active) {
      if (e.dead || e.isBoss) continue;
      const dx = this.snareX - e.x, dy = this.snareY - e.y, d2 = dx * dx + dy * dy;
      if (!defend && d2 <= zr2) inZoneEnemies++;   // нужен только для розжига (kindle); в defend не считаем
      if (defend && d2 <= chr2) coreAttackers++;
      if (d2 <= pr2 && d2 > 1) { const d = Math.sqrt(d2); e.x += dx / d * pullForce * dt; e.y += dy / d * pullForce * dt; }
    }
    if (defend) {
      // ОБОРОНА: враги у ядра подтачивают целостность; суммарный входящий урон ограничен cap (сглаживание спайка)
      if (coreAttackers > 0) {
        const incoming = Math.min(coreAttackers * S.defend.coreDpsPerEnemy, S.defend.coreDamageCap);
        this.snareCoreHp -= incoming * dt;
        if (this.snareCoreHp <= 0) { this.snareCoreHp = 0; this.endSnare(false); return; }   // ядро погасло → ПРОВАЛ
      }
      // СЛОЙ 1: машина волн — боевая фаза → пауза (передышка на угли) → следующая волна; выстоял все → успех
      this.snareWaveT -= dt;
      if (this.snareWavePhase === 'fighting') {
        if (this.snareWaveT <= 0) {
          if (this.snareWave >= S.defend.waves) { this.endSnare(true); return; }   // выстоял все волны → успех
          this.snareWavePhase = 'gap'; this.snareWaveT = S.defend.waveGap;
          this.banner(t('banner.snareWaveCleared'), 1.2); Audio2.levelup();          // волна отбита — передышка
        }
      } else if (this.snareWaveT <= 0) {                                            // пауза вышла → следующая волна
        this.snareWave++; this.snareWavePhase = 'fighting'; this.snareWaveT = S.defend.waveDur;
        this.spawnSnareWave(this.snareWave); this.addShake(8);
      }
    } else {
      // РОЗЖИГ: в зоне растёт (враги замедляют, глубже медленнее), вне зоны — спадает
      if (dist2(p.x, p.y, this.snareX, this.snareY) <= zr2) {
        const slowMul = Math.max(S.slowFloor, 1 - inZoneEnemies * S.enemySlow);
        const depthMul = 1 / (1 + (this.depthIndex || 0) * S.depthRateMul);
        this.snareKindle = Math.min(1, this.snareKindle + S.kindleRate * slowMul * depthMul * dt);
      } else {
        this.snareKindle = Math.max(0, this.snareKindle - S.kindleDecay * dt);
      }
    }
    // ПУЛЬСАР: волны жара — в defend центрированы на ЯДРЕ (авто-защита: чистит атакующих ядро), в kindle — вокруг игрока
    if (b && b.pulseCd) {
      this.snarePulseT -= dt;
      if (this.snarePulseT <= 0) {
        this.snarePulseT += b.pulseCd;
        const ox = defend ? this.snareX : p.x, oy = defend ? this.snareY : p.y;
        const r2 = b.pulseR * b.pulseR;
        for (const e of this.enemies.active) {
          if (e.dead) continue;
          if (dist2(e.x, e.y, ox, oy) <= r2) this.hitEnemy(e, b.pulseDmg, b.pulseKb, ox, oy);
        }
        this.effects.push({ kind: 'nova', x: ox, y: oy, r: 0, maxR: b.pulseR, life: 0.3, maxLife: 0.3, color: '#fff0a0' });
        this.addShake(4); Audio2.nova();
      }
    }
    // ИСХОД: kindle — полный розжиг → ранняя вспышка-успех; окно истекло → успех (kindle по остатку / defend если ядро живо)
    if (!defend && this.snareKindle >= 1) { this.endSnare(true); return; }
    if (this.snareT <= 0) this.endSnare(true);
  },

  endSnare(success) {
    const p = this.player, S = CONFIG.snare;
    const defend = this.snareMode === 'defend';
    // доля исхода → доля награды: kindle — по розжигу; defend — по остатку целостности ядра (частичный успех)
    const frac = defend ? (this.snareCoreMaxHp > 0 ? this.snareCoreHp / this.snareCoreMaxHp : 0) : this.snareKindle;
    this.snareT = 0; this.snareBoon = null; this.snareBoonDef = null; this.snareLightBonus = 0; this.snareKindle = 0;
    this.snareCoreHp = 0; this.snareCoreMaxHp = 0;
    p._snareDR = 1; p._snareCdMul = 1; p._snareDmgMul = 1;
    if (success) {
      // награда масштабируется исходом (пол rewardFloor — даже слабый успех щедр) и глубиной
      const mul = clamp(frac, S.rewardFloor, 1) * (1 + (this.depthIndex || 0) * S.depthRewardMul);
      const goldTot = Math.round(S.rewardGold * mul), gems = Math.max(1, Math.round(S.rewardGems * mul));
      for (let i = 0; i < 12; i++) this.spawnPickup(p.x + rand(-70, 70), p.y + rand(-70, 70), 'gold', Math.round(goldTot / 12) || 5);
      for (let i = 0; i < gems; i++) this.spawnPickup(p.x + rand(-60, 60), p.y + rand(-60, 60), 'xpbig', CONFIG.xp.gemBigValue);
      p.light = Math.min(CONFIG.light.max, p.light + S.rewardLight * mul);
      this.burst(p.x, p.y, '#ffd24a', 44, 320); this.addShake(12);
      this.banner(t(defend ? 'banner.snareDefendWin' : 'banner.snareWin'), 2.2); Audio2.bossDie();
    } else {
      // ПРОВАЛ (только defend): ядро погасло — снимает долю ТЕКУЩЕГО HP (×0.33, пол 1 → НЕ смертельно) + ноль награды
      p.hp = Math.max(1, p.hp * (1 - S.defend.failHpLoss));
      p.flash = 0.14; p._coreKick = 1; p.invuln = CONFIG.player.iframes;   // висцеральный удар + краткие iframes (не зачейнить в реелинге)
      this.burst(this.snareX, this.snareY, '#6a3530', 28, 200); this.addShake(16);
      this.banner(t('banner.snareFail'), 2.2); Audio2.hurt();
    }
    this.embers = []; this.emberBudget = 0; this._emberQueue = [];   // угли живут только окно обороны
    this.snareWave = 0; this.snareWavePhase = 'fighting'; this.snareWaveT = 0;   // сброс машины волн
  },

  // ПРОТОТИП свет-маятника: поставить УГОЛЁК — копию следующего оружия билда стационарно в точке игрока.
  placeEmber() {
    const p = this.player;
    if (this.emberBudget <= 0 || !this._emberQueue.length) return;
    const wk = this._emberQueue.shift();
    const def = weaponDef(wk.key);
    this.embers.push({
      x: p.x, y: p.y, key: wk.key, level: wk.level, timer: 0, kind: def.kind,
      // src = «игрок» для методов оружия (читают параметр, не глобал): своя точка + свой свет-радиус + снимок мощи билда
      src: { x: p.x, y: p.y, light: CONFIG.snare.defend.emberRadius, lastDir: null, damageMult: p.damageMult },
    });
    this.emberBudget--;
    this.burst(p.x, p.y, '#ffcf6e', 16, 140); this.addShake(4); Audio2.levelup();
  },

  // ПРОТОТИП свет-маятника: постановка по клавише E (один за нажатие) + угли стреляют из своих точек.
  updateEmbers(dt) {
    if (this.snareMode === 'defend' && this.snareT > 0 && Input.wasPressed('e')) {
      this.placeEmber(); Input.pressed['e'] = false;   // консьюмим: fixed-step может тикнуть несколько раз за кадр
    }
    if (!this.embers.length) return;
    for (const em of this.embers) {
      const def = weaponDef(em.key), lvl = em.level - 1;
      em.timer -= dt;
      if (em.timer <= 0) {
        em.timer += def.cooldown[lvl];
        if (def.kind === 'bolt') Weapons._bolt(em.src, em, def, lvl);
        else if (def.kind === 'chain') Weapons._chain(em.src, em, def, lvl);
        else if (def.kind === 'lantern') Weapons._lantern(em.src, em, def, lvl);
      }
    }
  },

  // СЛОЙ 1: спавн боевой волны — растущий батч врагов от краёв к ядру (обычный спавнер заглушён, волны = весь спавн).
  spawnSnareWave(n) {
    const D = CONFIG.snare.defend;
    const count = D.waveBaseCount + (n - 1) * D.waveGrowth;
    const ph = Spawner.currentPhase(this.time);
    for (let i = 0; i < count; i++) {
      const a = rand(0, TAU), r = CONFIG.snare.pullRadius * rand(0.72, 1.0);
      Spawner.spawnTypeAt('chaser', this.snareX + Math.cos(a) * r, this.snareY + Math.sin(a) * r, ph, false);
    }
  },

  // вход в «говорящую фигуру»: мир замирает (update тикает только updateSpeak), фигура с фразой
  enterSpeak(kind, voice, x, y) {
    this.speak = { kind, voice, x, y, t: 0, dur: 3.0 };
    this.state = 'speak';
    this.dmgNumbers.clear();
    this.bannerTime = 0;
    Audio2.levelup();   // вдох/появление фигуры
  },
  updateSpeak(dt) {
    if (!this.speak) { this.state = 'playing'; return; }
    this.speak.t += dt;
    if (this.speak.t >= this.speak.dur) this.dismissSpeak();   // авто-уход (клик пропускает раньше — main.ts)
  },
  dismissSpeak() {
    const s = this.speak; this.speak = null;
    this.state = 'playing';
    if (typeof Input !== 'undefined') Input.mouseDown = false;   // не «поехать» к курсору после клика-пропуска
    if (!s) return;
    const p = this.player;
    if (s.kind === 'cache') {
      // Тайник: щедрый дождь золота + всплеск света
      for (let i = 0; i < 20; i++)
        this.spawnPickup(p.x + rand(-70, 70), p.y + rand(-70, 70), 'gold', 5);
      p.light = Math.min(CONFIG.light.max, p.light + 200);
      this.burst(p.x, p.y, CONFIG.colors.gold, 36, 280);
      this.addShake(8);
      Audio2.bossDie();
    } else if (s.kind === 'font') {
      // Источник: лечение + временный мощный баф урона (0.4→0.25: хил — передышка, не полный сброс ошибок)
      p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.25);
      this.bless = { dmg: 1.6, life: CONFIG.shrines.blessLife, maxLife: CONFIG.shrines.blessLife };
      p.light = Math.min(CONFIG.light.max, p.light + 120);
      this.burst(p.x, p.y, '#7CFFB2', 40, 300);
      this.addShake(8);
      Audio2.levelup();
    }
  },

  generateRelicOffers() {
    const owned = new Set(this.relicsOwned);
    const avail = Object.keys(CONFIG.relics).filter(k => !owned.has(k));
    // Реликварий (мета): +1 вариант реликвии в выборе святилища (больше ВЫБОРА, не силы)
    const n = 3 + ((typeof Meta !== 'undefined') ? Meta.upgLevel('trove') : 0);
    const chosen: any[] = sampleN(avail, n).map(key => ({ type: 'relic', key }));
    while (chosen.length < n) chosen.push({ type: 'heal' });   // реликвии кончились — лечение
    return chosen;
  },

  // применить реликвию: мутирует базу игрока (+ recalc) либо ставит флаг боевого хука
  applyRelic(key) {
    const r = CONFIG.relics[key];
    if (!r || this.relicsOwned.includes(key)) return;   // не применять одну реликвию дважды
    this.relicsOwned.push(key);
    const p = this.player;
    if (r.dmg)    p.base.damageMult *= r.dmg;
    if (r.maxHp)  p.base.maxHp += r.maxHp;
    if (r.regen)  p.base.regen += r.regen;
    if (r.speed)  p.base.moveSpeed *= r.speed;
    if (r.magnet) p.base.pickupRadius *= r.magnet;
    if (r.light)  p._charLightBonus += r.light;
    if (r.leech)  p._relicLeech = (p._relicLeech || 0) + r.leech;
    if (r.cdMul)  p._relicCdMul = (p._relicCdMul || 1) * r.cdMul;
    if (r.gold)   this.goldMult *= r.gold;
    if (r.aura)   p._auraThorns = true;
    if (r.loEdge) p._loEdge = (p._loEdge || 0) + r.loEdge;   // билд-ось Границы: реликвия тёмного билда
    if (r.hiEdge) p._hiEdge = (p._hiEdge || 0) + r.hiEdge;   // реликвия светлого билда
    p.recalc();
    // Сияние (+радиус света): мгновенный скачок + вспышка, чтобы прирост был ЗАМЕТЕН
    // (жалоба плейтеста: «+90 света не почувствовал»)
    if (r.light) {
      p.light = Math.min(CONFIG.light.max, p.light + r.light);
      this.burst(p.x, p.y, CONFIG.colors.player, 34, 280);
    }
    this.banner(t('banner.relic', { name: tName('relics', key, r.name) }), 2.2);
    this.addShake(8);
  },

  // Венец гнева (реликвия thorns): свет периодически жжёт тьму внутри радиуса света
  updateAura(dt) {
    this._auraTick = (this._auraTick || 0) - dt;
    if (this._auraTick > 0) return;
    this._auraTick += 0.45;
    const p = this.player;
    const r2 = p.light * p.light;
    for (const e of this.enemies.active) {
      if (e.dead || e.isBoss) continue;
      if (dist2(e.x, e.y, p.x, p.y) <= r2) this.hitEnemy(e, 16, 0, p.x, p.y);
    }
  },

  // CLICK-TO-MOVE: клик мышью назначает точку-цель, герой идёт туда сам (Input.moveVector читает moveTarget).
  // Лёгкая вспышка-маркер (расширяющееся тёплое кольцо) подтверждает точку клика.
  setMoveTarget(wx, wy) {
    this.moveTarget = { x: wx, y: wy };
    this.effects.push({ kind: 'nova', x: wx, y: wy, r: 0, maxR: 24, life: 0.4, maxLife: 0.4, color: '#ffd27a' });
  },

  updatePlayer(dt) {
    const p = this.player;
    const mv = Input.moveVector();
    // ТЕНЁТА слой A: корень снят — во время удержания игрок МОБИЛЕН в зоне розжига (TD-оборона, не якорь)
    p.vx = mv.x * p.moveSpeed;
    p.vy = mv.y * p.moveSpeed;
    if (mv.x || mv.y) { p.lastDir.x = mv.x; p.lastDir.y = mv.y; }
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    this.updateEmberTrail();   // выжженная земля: где прошло ядро — остаётся тёплая память формы
    if (p.regen > 0 && p.hp < p.maxHp) p.hp = Math.min(p.maxHp, p.hp + p.regen * dt);
    if (p.invuln > 0) p.invuln -= dt;
    if (p.flash > 0) p.flash -= dt;
    // «пинок» ядра: при ударе пламя резко сжимается и спружинивает обратно (§8.3 — физическая боль без анимации)
    if (p._coreKick > 0) p._coreKick = Math.max(0, p._coreKick - dt / 0.28);
    // «вспышка роста»: на новом уровне ядро вспыхивает ослепительным белым и опадает (§10.4 бит3)
    if (p._coreFlash > 0) p._coreFlash = Math.max(0, p._coreFlash - dt / 0.55);
    // свет: цель растёт с уровнем; модифицируется глубиной, аномалиями, дыханием
    // День/Ночь и пятнами тьмы; восстанавливается после проседаний от урона.
    // У героев-режимов (Угасающий/Зеркало) — своя модель света.
    const L = CONFIG.light;
    const rm = this.runMods || { light: 1, recover: 1 };
    const recoverMul = (this._lightRecoverMul != null) ? this._lightRecoverMul : 1;  // глушение Рассеивателем
    // суммарное подавление пятнами тьмы (Якори), если игрок внутри
    let supp = 1;
    if (this.darkZones.length) {
      for (const z of this.darkZones) if (dist2(p.x, p.y, z.x, z.y) <= z.r * z.r) supp *= 0.6;
    }
    // ОВЕРКЛОК (горн-класс B): каждый стак перегрева жжёт свет → moat у́же, дрейф к тёмному полюсу.
    // Кап (maxDrain): мульти-оверклок не залочивает свет на min навсегда — ось остаётся живой.
    let ocDrain = 0;
    for (const w of p.weapons) if (w.oc) ocDrain += w.oc;
    ocDrain = Math.min(ocDrain * CONFIG.overclock.lightCost, CONFIG.overclock.maxDrain);
    if (p._lightMode === 'decay') {
      // Угасающий: свет непрерывно тает; зоны тьмы ускоряют пропорционально; убийства подливают
      let rate = p._lightDecay;
      if (supp < 1) rate *= (2 - supp);
      // ОВЕРКЛОК и в decay-режиме: тут модель света рейтовая (не target-based), поэтому цена —
      // снижение ПОТОЛКА (L.max−ocDrain), а не сдвиг равновесия как в обычном режиме. Намеренный сплит.
      p.light = clamp(p.light - rate * dt, L.min, Math.max(L.min, L.max - ocDrain));
    } else {
      // Зеркало: радиус из осколков-убийств; иначе — обычный рост с уровнем
      const baseT = (p._lightMode === 'mirror')
        ? (L.min + p._mirrorStacks * p._mirrorPer)
        : (L.base + (p.level - 1) * L.perLevel + (p._charLightBonus || 0));
      // ЧЕМ МЕНЬШЕ HP — ТЕМ УЖЕ ЗОНА ВИДИМОСТИ: ранение сжимает свет (1.0 при полном HP → 0.68 при смерти).
      // Мягко, и финальный clamp к [min,max] держит пол (свет не гаснет в ноль — без спирали смерти). Тюнинг-ручка.
      const hpLightMul = clamp(0.68 + 0.32 * (p.hp / p.maxHp), 0.68, 1);
      let target = clamp(baseT, L.min, L.max) * rm.light * this.dayNightMult() * supp * hpLightMul;
      target = clamp(target + (this.snareLightBonus || 0), L.min, L.max);   // ТЕНЁТА: светлый полюс держит зону шире
      if (ocDrain) target = Math.max(L.min, target - ocDrain);              // ОВЕРКЛОК: цена света за мощь
      if (p.light < target) p.light = Math.min(target, p.light + L.recover * rm.recover * recoverMul * dt);
      else if (p.light > target) p.light = target;
    }
    // ПРИМАНКА (активный глагол): пока зажата клавиша и нет кулдауна — ФОРСИРОВАННО гасим сферу к min
    // (поверх обычной модели света), копим время; по holdTime — срыв в burst-вспышку. Отпустил — сброс.
    const LR = CONFIG.lure;
    if (this._lureCd > 0) this._lureCd -= dt;
    const lureHeld = p.alive && this._lureCd <= 0 &&
      (typeof Input !== 'undefined' && Input.keys && !!Input.keys[LR.key]);
    if (lureHeld) {
      p.light = Math.max(L.min, p.light - L.recover * LR.drainMul * dt);
      this._lureT += dt;
      if (this._lureT >= LR.holdTime) { this.lureBurst(); this._lureT = 0; this._lureCd = LR.cooldown; }
    } else {
      this._lureT = 0;
    }
    // всплеск света от ульты (Рассветная вспышка) держит радиус ~2с во всех режимах
    if (p._lightFloorT > 0) { p._lightFloorT -= dt; if (p.light < p._lightFloor) p.light = p._lightFloor; }
    if (p.light < L.min) p.light = L.min;
    this.camera.x = p.x; this.camera.y = p.y;
  },

  // ВЫЖЖЕННАЯ ЗЕМЛЯ: метки тёплой памяти формы кладутся по пути ядра (только в движении),
  // выветриваются за life секунд. Кольцевой буфер держит перф и читаемость следа. Без RNG → детерм.
  updateEmberTrail() {
    const p = this.player, EF = CONFIG.emberField, f = this.emberField;
    for (let i = f.length - 1; i >= 0; i--) if (this.time - f[i].born >= EF.life) f.splice(i, 1);
    const last = this._emberLast;
    if (!last || dist2(p.x, p.y, last.x, last.y) >= EF.emitDist * EF.emitDist) {
      f.push({ x: p.x, y: p.y, born: this.time });
      if (f.length > EF.maxMarks) f.shift();
      this._emberLast = { x: p.x, y: p.y };
    }
    // плотность СВОЕЙ территории под игроком (синергия D2 тёмного билда): сколько зон перекрывают игрока
    let dens = 0; const r2 = EF.radius * EF.radius;
    for (let i = 0; i < f.length; i++) if (dist2(p.x, p.y, f[i].x, f[i].y) <= r2) dens++;
    p._emberDensity = dens;
  },

  // враг внутри какой-либо зоны выжженной земли?
  _enemyInEmber(e) {
    const f = this.emberField; if (!f.length) return false;
    const r2 = CONFIG.emberField.radius * CONFIG.emberField.radius;
    for (let i = 0; i < f.length; i++) if (dist2(e.x, e.y, f[i].x, f[i].y) <= r2) return true;
    return false;
  },

  updateEnemies(dt) {
    const p = this.player;
    const rm = this.runMods || { darkSpeed: 1, hitLoss: 1 };
    const list = this.enemies.active;
    let leechNear = false;
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (e.dead) continue;
      if (e.flash > 0) e.flash -= dt;
      if (e.hitCd > 0) e.hitCd -= dt;
      if (e.dmgCd > 0) e.dmgCd -= dt;
      if (e.dmgNumCd > 0) e.dmgNumCd -= dt;
      const d2p = dist2(e.x, e.y, p.x, p.y);
      // Иней (пассивка): аура вокруг игрока замедляет тьму
      if (p.frostR > 0 && !e.isBoss && d2p <= p.frostR * p.frostR) { e.slowT = 0.12; e.slowMul = p.frostMul; }
      // ВТОРАЯ ФАЗА БОССА: HP ниже порога → обретает новый примитив (рекомбинация арсенала врагов).
      if (e.isBoss && e.phase2 && e.bossPhase === 0 && e.hp <= e.maxHp * e.phase2.atHp) {
        e.bossPhase = 1;
        const f = e.phase2;
        if (f.lunge) { e.lunge = true; e.lungeRange = f.lunge.range; e.lungeSpeed = f.lunge.speed; e.lungeCd = f.lunge.cd; e.lungeTimer = f.lunge.cd * 0.4; e.lungeT = 0; }
        if (f.drain) { e.drainLight = f.drain.light; e.drainRange = f.drain.range; }
        if (f.slam) { e.slam = true; e.slamR = f.slam.radius; e.slamDmg = f.slam.dmg * (rm.dmg || 1); e.slamCd = f.slam.cd; e.slamTimer = f.slam.cd * 0.5; e.slamTele = -1; }
        if (f.ringRush && e.ring) { e.ringCd *= f.ringRush.cdMul; e.ringShots += f.ringRush.shotsAdd; }
        const bn = (CONFIG.enemies[e.typeKey] ? tName('enemies', e.typeKey, CONFIG.enemies[e.typeKey].name) : '').toUpperCase();
        this.banner(t('banner.enrage', { name: bn }), 1.8);
        this.addShake(14); Audio2.boss();
      }
      // движение к игроку
      let sp = e.speed;
      if (e.isBoss) {
        // НАСТОЯЩИЙ бой: от босса нельзя просто уйти шагом (жалоба плейтеста).
        const bd = Math.sqrt(d2p);
        const leash = Math.hypot(Game.viewW, Game.viewH) * 0.62;
        if (bd > leash) {
          // крайняя мера: игрок ускакал за экран — босс выныривает из тьмы у кромки (не теряется)
          const back = angleTo(p.x, p.y, e.x, e.y);
          const nr = Math.hypot(Game.viewW / 2, Game.viewH / 2) + 40;
          e.x = p.x + Math.cos(back) * nr; e.y = p.y + Math.sin(back) * nr;
          e.kx = 0; e.ky = 0;
          this.burst(e.x, e.y, e.color, 24, 220);   // вынырнул из тьмы
        } else if (bd > 280) {
          sp *= 1 + Math.min(2.2, (bd - 280) / 360);   // резинка: чем дальше — тем быстрее догоняет
        }
        // полюс-чтение: разъярённый босс (2-я фаза) быстрее при ВЫСОКОМ свете игрока — давит светлый билд
        if (e.bossPhase === 1) sp *= 1 + 0.5 * clamp(p.light / CONFIG.light.max, 0, 1);
      } else if (d2p > p.light * p.light) {
        // твист: вне света враги быстрее. «Тьма признаёт своего»: взявшему тёмный полюс
        // (loEdge — Ярость углей / Завет пепла) рывок тьмы мягче — иначе нерф скорости
        // игрока (215→178) делает тёмный билд нежизнеспособным (рой 128×1.35 ≈ догоняет).
        const dsm = (p._loEdge > 0) ? CONFIG.light.darkSpeedMultLo : CONFIG.light.darkSpeedMult;
        sp *= dsm * rm.darkSpeed;
      }
      // HG-10 Срыв: рывок в упор. Вне рывка — обычный шаг; накопил кулдаун и игрок в зоне → бросок,
      // sp подменяется на lungeSpeed на короткое окно (надо успеть сжечь burst-ом до контакта).
      if (e.lunge) {
        if (e.lungeT > 0) { e.lungeT -= dt; sp = e.lungeSpeed; }
        else { e.lungeTimer -= dt; if (e.lungeTimer <= 0 && d2p <= e.lungeRange * e.lungeRange) { e.lungeTimer = e.lungeCd; e.lungeT = 0.45; sp = e.lungeSpeed; } }
      }
      // HG-9: ЖАР СВЕТА — враги В радиусе света горят, урон ∝ силе света (light/max). Делает ось
      // «ты=свет» живым оружием (свет-билд = поле-killzone), не декоративной. Боссы иммунны (флэт-dps
      // не должен тривиализировать бой — их жжёт оружие). Числа не спамим: смерть даёт ash-burst.
      // heatImmune (Срыв) проходит сквозь поле — его берёт только burst оружия. heatEdgeAmp: сильное
      // killzone-поле — подпись СВЕТЛОГО полюса (hiEdge), нейтраль жжёт лишь базой (HG-11).
      if (!e.isBoss && !e.heatImmune && CONFIG.light.heatDps && d2p <= p.light * p.light) {
        const heatMul = 1 + (CONFIG.light.heatEdgeAmp || 0) * (p._hiEdge || 0);
        e.hp -= CONFIG.light.heatDps * clamp(p.light / CONFIG.light.max, 0, 1) * heatMul * dt;
        if (e.hp <= 0) { this.killEnemy(e); continue; }
      }
      // угол к игроку считаем ПОСЛЕ возможного телепорта босса
      const a = angleTo(e.x, e.y, p.x, p.y);
      // HG-10 Жало: пятится, когда игрок ближе kiteRange (держит дистанцию для выстрела) — достаёшь
      // его широким светлым полем; малое тёмное ядро вынуждено гнаться на открытое.
      const retreat = e.kite && d2p < e.kiteRange * e.kiteRange;
      // дальник тормозит в дистанции боя, чтобы держать позицию и стрелять (но не когда пятится)
      if (e.ranged && !retreat && d2p < (e.shotRange * 0.75) * (e.shotRange * 0.75)) sp *= 0.25;
      // замедление в свет-зоне фонаря
      if (e.slowT > 0) { sp *= e.slowMul; e.slowT -= dt; }
      // ВЫЖЖЕННАЯ ЗЕМЛЯ: в зоне памяти формы холодный рой слабее — тление (и замедление, если включат).
      // Боссы иммунны И к тлению, И к замедлению (slow=1.0 — выкл; рычаг оставлен для позднего касания) —
      // след не должен тривиализировать бой.
      if (this.emberField.length && this._enemyInEmber(e)) {
        sp *= CONFIG.emberField.slow;
        if (CONFIG.emberField.dotPct > 0 && !e.isBoss) {
          e.hp -= e.maxHp * CONFIG.emberField.dotPct * dt;
          if (e.hp <= 0) { this.killEnemy(e); continue; }
        }
      }
      // Пожиратель высасывает радиус света, пока близко (приоритет «убить первым»)
      if (e.drainLight && d2p <= e.drainRange * e.drainRange) {
        p.light = Math.max(CONFIG.light.min, p.light - e.drainLight * dt);
      }
      // Рассеиватель: глушит восстановление света, пока рядом
      if (e.suppressLight && d2p <= e.suppressRange * e.suppressRange) leechNear = true;
      // Нулевая Точка: пульсирует кольцами тьмы (радиальный залп по таймеру)
      if (e.ring) {
        e.ringTimer -= dt;
        if (e.ringTimer <= 0) {
          e.ringTimer += e.ringCd;
          for (let k = 0; k < e.ringShots; k++) {
            const ra = k / e.ringShots * TAU + this.time * 0.7;
            this.projectiles.spawn((pr) => {
              pr.x = e.x; pr.y = e.y;
              pr.vx = Math.cos(ra) * e.ringSpeed; pr.vy = Math.sin(ra) * e.ringSpeed;
              pr.kind = 'eshot'; pr.hostile = true; pr.dmg = e.ringDmg;
              pr.color = '#c08bff'; pr.radius = e.ringRadius; pr.life = 4; pr.hitIds = null;
            });
          }
          Audio2.nova();
        }
      }
      // Исполин: СЛЭМ по площади. Телеграф (красное растущее кольцо 0.7с) → удар + откидывание роя.
      // Заставляет покинуть радиус: стоять под гигантом нельзя.
      if (e.slam) {
        if (e.slamTele >= 0) {
          e.slamTele -= dt;
          if (e.slamTele <= 0) {
            e.slamTele = -1;
            if (p.invuln <= 0 && d2p <= e.slamR * e.slamR) {
              p.hp -= e.slamDmg * (p._snareDR || 1); p.invuln = CONFIG.player.iframes; p.flash = 0.12; p._coreKick = 1;
              p.light = Math.max(CONFIG.light.min, p.light - CONFIG.light.hitLoss * rm.hitLoss);
              this.addShake(CONFIG.feel.shakeOnHit); Audio2.hurt();
              if (p.hp <= 0) { p.hp = 0; this.gameOver(); return; }
            }
            for (let k = 0; k < list.length; k++) {
              const o = list[k]; if (o.dead || o.isBoss) continue;
              const dx = o.x - e.x, dy = o.y - e.y, dd = Math.hypot(dx, dy) || 1;
              if (dd < e.slamR) { const f = 420 * (1 - dd / e.slamR); o.kx += dx / dd * f; o.ky += dy / dd * f; }
            }
            this.effects.push({ kind: 'nova', x: e.x, y: e.y, r: 0, maxR: e.slamR, life: 0.4, maxLife: 0.4, color: '#ff7a3a' });
            this.addShake(16); Audio2.nova();
          }
        } else {
          e.slamTimer -= dt;
          if (e.slamTimer <= 0 && d2p <= (e.slamR * 1.3) * (e.slamR * 1.3)) {
            e.slamTimer = e.slamCd; e.slamTele = 0.7;
            this.effects.push({ kind: 'nova', x: e.x, y: e.y, r: 0, maxR: e.slamR, life: 0.7, maxLife: 0.7, color: '#ff3030' });
            this.addShake(5);
          }
        }
      }
      const ma = retreat ? a + Math.PI : a;
      let mvx = Math.cos(ma) * sp, mvy = Math.sin(ma) * sp;
      e.x += (mvx + e.kx) * dt;
      e.y += (mvy + e.ky) * dt;
      // затухание нокбэка
      const fr = Math.exp(-10 * dt);
      e.kx *= fr; e.ky *= fr;

      // дальник стреляет по игроку
      if (e.ranged) {
        e.shotTimer -= dt;
        if (e.shotTimer <= 0 && d2p <= e.shotRange * e.shotRange) {
          e.shotTimer = e.shotCd;
          const sa = angleTo(e.x, e.y, p.x, p.y);
          this.projectiles.spawn((pr) => {
            pr.x = e.x; pr.y = e.y;
            pr.vx = Math.cos(sa) * e.shotSpeed; pr.vy = Math.sin(sa) * e.shotSpeed;
            pr.kind = 'eshot'; pr.hostile = true; pr.dmg = e.shotDmg;
            pr.color = '#c08bff'; pr.radius = e.shotRadius; pr.life = 3; pr.hitIds = null;
          });
        }
      }

      // контакт с игроком
      const rr = e.radius + p.radius;
      if (dist2(e.x, e.y, p.x, p.y) <= rr * rr) {
        if (p.invuln <= 0) {
          p.hp -= e.damage * (p._snareDR || 1);   // Бастион (опора захвата) режет входящий урон
          p.invuln = CONFIG.player.iframes;
          p.flash = 0.12;
          p._coreKick = 1;   // ядро-пламя сжимается от удара (§8.3)
          p.light = Math.max(CONFIG.light.min, p.light - CONFIG.light.hitLoss * rm.hitLoss); // тьма наступает
          if (p._lightMode === 'mirror') p._mirrorStacks = Math.floor(p._mirrorStacks * 0.5);  // осколки бьются
          this.addShake(CONFIG.feel.shakeOnHit);
          Audio2.hurt();
          // оттолкнуть врага
          const ka = angleTo(p.x, p.y, e.x, e.y);
          e.kx += Math.cos(ka) * CONFIG.player.contactKnockback;
          e.ky += Math.sin(ka) * CONFIG.player.contactKnockback;
          if (p.pulseDmg > 0) this.pulseWave();   // Световой пульс: волна в ответ на удар
          if (p.hp <= 0) { p.hp = 0; this.gameOver(); return; }
        }
      }
    }
    // Рассеиватель рядом → восстановление света заглушено (читается в updatePlayer)
    this._lightRecoverMul = leechNear ? 0.3 : 1;
    this.separateEnemies();
  },

  // лёгкое расталкивание, чтобы враги не схлопывались в точку (по сетке)
  separateEnemies() {
    const list = this.enemies.active;
    const N = list.length;
    if (N < 2) return;
    const cell = 36;
    const grid = this._grid || (this._grid = new Map());
    grid.clear();
    for (let i = 0; i < N; i++) {
      const e = list[i];
      if (e.dead || e.isBoss) continue;
      const cx = Math.floor(e.x / cell), cy = Math.floor(e.y / cell);
      const key = cx + ',' + cy;
      let arr = grid.get(key);
      if (!arr) { arr = []; grid.set(key, arr); }
      arr.push(e);
    }
    for (let i = 0; i < N; i++) {
      const e = list[i];
      if (e.dead || e.isBoss) continue;
      const cx = Math.floor(e.x / cell), cy = Math.floor(e.y / cell);
      for (let ox = -1; ox <= 1; ox++) for (let oy = -1; oy <= 1; oy++) {
        const arr = grid.get((cx + ox) + ',' + (cy + oy));
        if (!arr) continue;
        for (const o of arr) {
          if (o === e || o.dead) continue;
          const dx = e.x - o.x, dy = e.y - o.y;
          const minD = e.radius + o.radius;
          const d2 = dx * dx + dy * dy;
          if (d2 > 0 && d2 < minD * minD) {
            const d = Math.sqrt(d2);
            const push = (minD - d) * 0.25;
            const nx = dx / d, ny = dy / d;
            e.x += nx * push; e.y += ny * push;
            o.x -= nx * push; o.y -= ny * push;
          }
        }
      }
    }
  },

  updateProjectiles(dt) {
    const list = this.projectiles.active;
    // длину фиксируем ДО цикла: осколки «Раскола» спавнятся в этот же массив, но обрабатываются
    // со следующего кадра (иначе получили бы частичный апдейт из точки спавна в кадр рождения).
    const len = list.length;
    for (let i = 0; i < len; i++) {
      const p = list[i];
      if (p.dead) continue;
      if (p.kind === 'mine') { this.updateMine(p, dt); continue; }
      if (p.kind === 'lantern') { this.updateLantern(p, dt); continue; }
      if (p.kind === 'ricochet') { this.updateRicochet(p, dt); continue; }
      if (p.hostile) { this.updateHostile(p, dt); continue; }
      if (p.kind === 'bolt') {
        // самонаведение
        if (p.target && p.target.dead) p.target = this.nearestEnemy(p.x, p.y, 700);
        if (p.target && !p.target.dead) {
          const desired = angleTo(p.x, p.y, p.target.x, p.target.y);
          let cur = Math.atan2(p.vy, p.vx);
          let diff = desired - cur;
          while (diff > Math.PI) diff -= TAU;
          while (diff < -Math.PI) diff += TAU;
          const turn = clamp(diff, -7 * dt, 7 * dt);
          const a = cur + turn;
          const sp = Math.hypot(p.vx, p.vy);
          p.vx = Math.cos(a) * sp; p.vy = Math.sin(a) * sp;
          p.angle = a;
        }
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.life -= dt;
        if (p.life <= 0) { p.dead = true; continue; }
        // столкновение
        for (const e of this.enemies.active) {
          if (e.dead) continue;
          if (p.hitIds && p.hitIds.has(e)) continue;
          const rr = p.radius + e.radius;
          if (dist2(p.x, p.y, e.x, e.y) <= rr * rr) {
            this.hitEnemy(e, p.dmg, p.knockback, p.x, p.y);
            if (p.split && !p.didSplit) { p.didSplit = true; this.spawnBoltShards(p, e); }   // ГЛАГОЛ «Раскол»
            if (p.pierce > 0) {
              if (!p.hitIds) p.hitIds = new Set();
              p.hitIds.add(e);
              p.pierce--;
            } else { p.dead = true; break; }
          }
        }
      }
    }
  },

  // ГЛАГОЛ «Раскол»: при попадании bolt-снаряд порождает 2 самонаводящихся осколка (один раз, без рекурсии).
  // Осколки используют тот же bolt-хоминг в updateProjectiles; split=false у них → не плодятся дальше.
  spawnBoltShards(parent, hitE) {
    const targets = this.nearestEnemies(parent.x, parent.y, 3, 700).filter(t => t !== hitE);
    const sp = Math.hypot(parent.vx, parent.vy) || 340;
    const baseAng = Math.atan2(parent.vy, parent.vx);   // один источник истины (скорость), без дубля с p.angle
    for (let i = 0; i < 2; i++) {
      const t = targets[i] || null;
      const ang = t ? angleTo(parent.x, parent.y, t.x, t.y) : baseAng + (i === 0 ? 0.6 : -0.6);
      this.projectiles.spawn((s) => {
        s.x = parent.x; s.y = parent.y;
        s.vx = Math.cos(ang) * sp; s.vy = Math.sin(ang) * sp;
        s.dmg = parent.dmg * 0.45;
        s.radius = parent.radius; s.life = 0.7; s.pierce = 0;
        s.color = parent.color; s.kind = 'bolt'; s.knockback = parent.knockback * 0.5;
        s.hitIds = new Set([hitE]);   // НЕ добивать врага, которого уже ударил родитель (тот же кадр)
        s.hostile = false; s.target = t; s.angle = ang;
        s.split = false; s.didSplit = true;   // осколки не раскалываются
      });
    }
  },

  // мина: ждёт, взрывается при враге рядом или по истечении жизни
  updateMine(p, dt) {
    p.life -= dt;
    let det = p.life <= 0;
    if (!det) {
      for (const e of this.enemies.active) {
        if (e.dead) continue;
        const rr = p.trigger + e.radius;
        if (dist2(p.x, p.y, e.x, e.y) <= rr * rr) { det = true; break; }
      }
    }
    if (det) {
      for (const e of this.enemies.active) {
        if (e.dead) continue;
        const rr = p.radius + e.radius;
        if (dist2(p.x, p.y, e.x, e.y) <= rr * rr) this.hitEnemy(e, p.dmg, p.knockback, p.x, p.y);
      }
      this.effects.push({ kind: 'nova', x: p.x, y: p.y, r: 0, maxR: p.radius, life: 0.35, maxLife: 0.35, color: p.color });
      this.addShake(4);
      Audio2.nova();
      p.dead = true;
    }
  },

  // Отражённый луч: летит, отскакивает от кромки света игрока (крепчая), бьёт врагов
  updateRicochet(p, dt) {
    p.x += p.vx * dt; p.y += p.vy * dt;
    p.life -= dt;
    if (p.life <= 0) { p.dead = true; return; }
    const pl = this.player;
    const dx = p.x - pl.x, dy = p.y - pl.y;
    const d = Math.hypot(dx, dy);
    // отскок от кромки тьмы (границы света), пока есть отскоки
    if (d > pl.light && p.bounces > 0 && d > 1) {
      const nx = dx / d, ny = dy / d;
      const dot = p.vx * nx + p.vy * ny;
      // только если снаряд реально летит НАРУЖУ — иначе движение игрока даёт фантомный отскок
      if (dot > 0) {
        p.vx -= 2 * dot * nx; p.vy -= 2 * dot * ny;
        p.x = pl.x + nx * (pl.light - 2); p.y = pl.y + ny * (pl.light - 2);
        p.bounces--;
        p.dmg *= (1 + p.bounceGain);
        if (p.hitIds) p.hitIds.clear();   // после отскока можно снова бить тех же
        Audio2.hit();
      }
    }
    for (const e of this.enemies.active) {
      if (e.dead) continue;
      if (p.hitIds && p.hitIds.has(e)) continue;
      const rr = p.radius + e.radius;
      if (dist2(p.x, p.y, e.x, e.y) <= rr * rr) {
        this.hitEnemy(e, p.dmg, p.knockback, p.x, p.y);
        if (!p.hitIds) p.hitIds = new Set();
        p.hitIds.add(e);
      }
    }
  },

  // фонарь (Пульс-фонарь): стоит на месте, замедляет и жжёт врагов в радиусе
  updateLantern(p, dt) {
    p.life -= dt;
    if (p.life <= 0) { p.dead = true; return; }
    p.tickTimer -= dt;
    const doDmg = p.tickTimer <= 0;
    if (doDmg) p.tickTimer += p.tick;
    for (const e of this.enemies.active) {
      if (e.dead) continue;
      const rr = p.radius + e.radius;
      if (dist2(p.x, p.y, e.x, e.y) <= rr * rr) {
        e.slowT = 0.12; e.slowMul = p.slowMul;        // замедление (читается в updateEnemies)
        if (doDmg && p.dmg > 0) this.hitEnemy(e, p.dmg, 0, p.x, p.y);
      }
    }
  },

  // вражеский снаряд: летит, бьёт игрока
  updateHostile(p, dt) {
    p.x += p.vx * dt; p.y += p.vy * dt;
    p.life -= dt;
    if (p.life <= 0) { p.dead = true; return; }
    const pl = this.player;
    const rr = p.radius + pl.radius;
    if (dist2(p.x, p.y, pl.x, pl.y) <= rr * rr) {
      if (pl.invuln <= 0) {
        pl.hp -= p.dmg * (pl._snareDR || 1);   // Бастион режет урон вражеского снаряда
        pl.invuln = CONFIG.player.iframes;
        pl.flash = 0.12;
        pl._coreKick = 1;   // ядро-пламя сжимается от удара (§8.3)
        pl.light = Math.max(CONFIG.light.min, pl.light - CONFIG.light.hitLoss * (this.runMods ? this.runMods.hitLoss : 1));
        if (pl._lightMode === 'mirror') pl._mirrorStacks = Math.floor(pl._mirrorStacks * 0.5);  // осколки бьются
        this.addShake(CONFIG.feel.shakeOnHit);
        Audio2.hurt();
        if (pl.pulseDmg > 0) this.pulseWave();
        if (pl.hp <= 0) { pl.hp = 0; this.gameOver(); }
      }
      p.dead = true;
    }
  },

  updatePickups(dt) {
    const p = this.player;
    const list = this.pickups.active;
    const pickR = p.pickupRadius * (this.runMods ? this.runMods.pickup : 1);  // аномалия Морок сужает магнит
    const pr2 = pickR * pickR;
    const aw = pickR * 1.6;                  // зона мягкого притяжения (шлейф; сужена 2.2→1.6)
    const aw2 = aw * aw;
    for (let i = 0; i < list.length; i++) {
      const k = list[i];
      if (k.dead) continue;
      const d2 = dist2(k.x, k.y, p.x, p.y);
      // осколок опыта остывает и гаснет ТОЛЬКО если заброшен ДАЛЕКО во тьме (за светом+буфер) и вне магнита:
      // declutter дальнего клаттера, но опыт в свете и у кромки не теряется (не бьёт по прогрессии/балансу)
      if (k.life > 0 && !k.magnet) {
        const farEdge = p.light + CONFIG.xp.gemFarBuffer;
        if (d2 > farEdge * farEdge) { k.life -= dt; if (k.life <= 0) { k.dead = true; continue; } }
      }
      if (k.magnet || d2 <= pr2) {
        k.magnet = true;
        const a = angleTo(k.x, k.y, p.x, p.y);
        const pull = 420;
        k.x += Math.cos(a) * pull * dt;
        k.y += Math.sin(a) * pull * dt;
      } else if (d2 <= aw2) {
        // мягкий шлейф: осколки медленно тянутся за игроком
        const a = angleTo(k.x, k.y, p.x, p.y);
        const pull = 110;
        k.x += Math.cos(a) * pull * dt;
        k.y += Math.sin(a) * pull * dt;
      }
      const rr = p.radius + 10;
      if (d2 <= rr * rr) {
        this.collect(k);
        k.dead = true;
      }
    }
  },

  collect(k) {
    const p = this.player;
    if (k.type === 'gold') { p.gold += Math.round(k.value * this.goldMult); Audio2.pickup(); return; }
    const xpMul = this.runMods ? this.runMods.xp : 1;
    const leveled = p.gainXp(xpMul !== 1 ? Math.max(1, Math.round(k.value * xpMul)) : k.value);
    if (leveled > 0) this.pendingLevels += leveled;
    // короткий пульс XP-бара (связь килл→осколок→XP читаема) — единый, рефрешим
    const pulse = this.effects.find(e => e.kind === 'xppulse');
    if (pulse) pulse.life = pulse.maxLife;
    else this.effects.push({ kind: 'xppulse', life: 0.3, maxLife: 0.3 });
    Audio2.pickup();
  },

  updateParticles(dt) {
    const list = this.particles.active;
    for (let i = 0; i < list.length; i++) {
      const pt = list[i];
      if (pt.dead) continue;
      pt.vy += pt.grav * dt;                          // угли всплывают (grav<0), осколки оседают (grav>0)
      pt.x += pt.vx * dt; pt.y += pt.vy * dt;
      const d = Math.exp(-pt.damp * dt);
      pt.vx *= d; pt.vy *= d;
      pt.life -= dt;
      pt.fade = Math.max(0, pt.life / pt.maxLife);
      if (pt.life <= 0) pt.dead = true;
    }
  },

  updateDmgNumbers(dt) {
    const list = this.dmgNumbers.active;
    for (let i = 0; i < list.length; i++) {
      const d = list[i];
      if (d.dead) continue;
      d.y += d.vy * dt;
      d.vy *= Math.exp(-2 * dt);
      d.life -= dt;
      if (d.life <= 0) d.dead = true;
    }
  },

  updateEffects(dt) {
    const list = this.effects;
    for (let i = list.length - 1; i >= 0; i--) {
      const e = list[i];
      e.life -= dt;
      if (e.kind === 'nova') e.r = e.maxR * (1 - e.life / e.maxLife);
      if (e.life <= 0) list.splice(i, 1);
    }
  },

  // ----------------------------- БОЙ -----------------------------
  hitEnemy(e, dmg, knockback, fromX, fromY) {
    if (e.dead) return;
    const p = this.player;
    // временный баф Источника (Святилище) усиливает весь урон
    if (this.bless) dmg *= this.bless.dmg;
    // крит-удар (пассивка Точность): ×2.2 и жёлтое число
    let crit = false;
    if (p.critChance > 0 && chance(p.critChance)) { dmg *= 2.2; crit = true; }
    e.hp -= dmg;
    e.flash = CONFIG.feel.hitFlash;
    if (knockback) {
      const kbScale = clamp(30 / e.maxHp, 0.03, 1);
      const a = angleTo(fromX, fromY, e.x, e.y);
      e.kx += Math.cos(a) * knockback * kbScale;
      e.ky += Math.sin(a) * knockback * kbScale;
    }
    // прореживание чисел урона: криты/боссы — всегда; обычные хиты не чаще раза в 0.28с
    // на врага (area/chain/orbit тикают пачками → жалоба «слишком много цифр»)
    if (crit || e.isBoss) {
      this.spawnDmgNumber(e.x, e.y - e.radius, Math.round(dmg), true);
    } else if (e.dmgNumCd <= 0) {
      this.spawnDmgNumber(e.x, e.y - e.radius, Math.round(dmg), false);
      e.dmgNumCd = 0.28;
    }
    Audio2.hit();
    if (e.hp <= 0) this.killEnemy(e);
  },

  killEnemy(e) {
    if (e.dead) return;
    e.dead = true;
    const pl = this.player;
    pl.kills++;
    // герои: Угасающий черпает свет из убийств, Зеркало копит осколки
    if (pl._lightMode === 'decay') pl.light = Math.min(CONFIG.light.max, pl.light + pl._killLight);
    else if (pl._lightMode === 'mirror') pl._mirrorStacks++;
    // Жатва света (пассивка/реликвия): лечит HP за убийство
    if (pl.leech > 0 && pl.hp < pl.maxHp) pl.hp = Math.min(pl.maxHp, pl.hp + pl.leech);
    // Вспышка (пассивка): убитый враг взрывается светом — урон по площади (цепная реакция).
    // Глубина рекурсии ограничена, чтобы плотная толпа не вызвала переполнение стека.
    if (pl.bloomDmg > 0 && (this._bloomDepth || 0) < 3) {
      this._bloomDepth = (this._bloomDepth || 0) + 1;
      this.effects.push({ kind: 'nova', x: e.x, y: e.y, r: 0, maxR: pl.bloomR, life: 0.28, maxLife: 0.28, color: '#ff9040' });
      try {
        for (const o of this.enemies.active) {
          if (o.dead || o === e) continue;
          const rr = pl.bloomR + o.radius;
          if (dist2(o.x, o.y, e.x, e.y) <= rr * rr) this.hitEnemy(o, pl.bloomDmg, 70, e.x, e.y);
        }
      } finally { this._bloomDepth--; }   // счётчик не утечёт даже при исключении в цепочке
    }
    this.score += e.score;
    if (this.runSeen) this.runSeen.add(e.typeKey);
    // опыт падает не всегда: шанс CONFIG.xp.dropChance (боссы — гарантированно, награда за долгий бой не должна быть монеткой)
    // СИДИРОВАННЫЙ chance() (не Math.random!) — иначе дроп опыта рандомит симуляцию и ломает детерминизм харнесса
    if (e.isBoss || chance(CONFIG.xp.dropChance))
      this.spawnPickup(e.x, e.y, e.bigGem ? 'xpbig' : 'xp', e.bigGem ? CONFIG.xp.gemBigValue : e.xp);
    const n = e.isBoss ? CONFIG.feel.bossDeathParticles : CONFIG.feel.deathParticles;
    this.ashBurst(e.x, e.y, n, e.isBoss);   // рассыпание в пепел/угли вместо неон-вспышки (§13)
    // Дробитель распадается на осколки
    if (e.split) {
      const ph = Spawner.currentPhase(this.time);
      for (let i = 0; i < e.splitCount; i++) {
        const a = rand(0, TAU), r = rand(10, 26);
        Spawner.spawnTypeAt(e.split, e.x + Math.cos(a) * r, e.y + Math.sin(a) * r, ph, true);
      }
    }
    // Якорь роняет пятно тьмы — гасит рост света на участке
    if (e.anchorOnDeath) {
      this.darkZones.push({ x: e.x, y: e.y, r: e.anchorRadius, life: e.anchorLife, maxLife: e.anchorLife });
    }
    if (e.isBoss) {
      this.killedBoss = true;
      this.bossKills++;
      this.addShake(CONFIG.feel.shakeOnBossDeath);
      const bn = CONFIG.enemies[e.typeKey]
        ? tName('enemies', e.typeKey, CONFIG.enemies[e.typeKey].name).toUpperCase()
        : t('hud.boss');
      this.banner(t('banner.bossDown', { name: bn }), 2.5);
      this.bossActive = false;
      Spawner.nextBossT = this.time + CONFIG.endless.bossEvery;   // кулдаун след. босса — от смерти, не от спавна
      Audio2.bossDie();
      Audio2.musicSetMood(this.reachedDawn ? 'dawn' : (this.depthIndex >= 3 ? 'deep' : 'night'));
      for (let i = 0; i < 10; i++)
        this.spawnPickup(e.x + rand(-40, 40), e.y + rand(-40, 40), 'gold', 5);
      // ДЕМО: климакс-таста окончена — хук на полную версию (после N-го босса)
      if (CONFIG.demo.enabled && this.bossKills >= CONFIG.demo.endAfterBoss && this.state === 'playing') this.demoComplete();
    } else {
      Audio2.kill();
    }
  },

  // ДЕМО-ХУК: первый владыка повержен → конец демо на ВЫСОКОЙ ноте + обещание полной версии.
  demoComplete() {
    this.reachedDawn = true;       // засчитать климакс демо как победную веху для меты
    this.awardRun();
    this.demoEnd = true;
    this.state = 'win';
    this.bannerTime = 0;
    Audio2.musicSetMood('dawn');
  },

  // ----------------------------- СПАВН ВСПОМОГ. -----------------------------
  spawnPickup(x, y, type, value) {
    const col = type === 'gold' ? CONFIG.colors.gold
              : type === 'xpbig' ? CONFIG.colors.xpBig : CONFIG.colors.xp;
    this.pickups.spawn((k) => {
      k.x = x; k.y = y; k.type = type; k.value = value;
      k.color = col; k.magnet = false; k.born = this.time;
      // опыт остывает и гаснет, если не подобран (declutter + тема «очаг света в холоде»); золото не гаснет
      k.life = (type === 'gold') ? 0 : CONFIG.xp.gemLife;
      const a = rand(0, TAU), s = rand(20, 70);
      k.vx = Math.cos(a) * s; k.vy = Math.sin(a) * s;
    });
  },

  burst(x, y, color, n, speed) {
    for (let i = 0; i < n; i++) {
      const a = rand(0, TAU), s = rand(speed * 0.3, speed);
      this.particles.spawn((pt) => {
        pt.x = x; pt.y = y;
        pt.vx = Math.cos(a) * s; pt.vy = Math.sin(a) * s;
        pt.life = pt.maxLife = rand(0.3, 0.7);
        pt.color = color; pt.size = rand(2, 4.5); pt.fade = 1;
        pt.grav = 0; pt.damp = 3;
      });
    }
  },

  // РАССЫПАНИЕ В ПЕПЕЛ (§13): враг не «лопается неоном», а осыпается остывающими углями+пеплом —
  // медленный дрейф, угли всплывают и гаснут, серый пепел оседает. Тёплые→серые, разный размер/жизнь.
  // ВАЖНО: частицы чисто косметические → используем ТОЛЬКО Math.random (не сидированный rand),
  // иначе переменное число вызовов rng десинхронит детерминизм симуляции.
  ashBurst(x, y, n, big) {
    // нагар: мир копит останки — тлеющая ash-метка на месте смерти (кап 30, гаснет ~5с; чисто визуал)
    if (!this.ashMarks) this.ashMarks = [];
    this.ashMarks.push({ x, y, born: this.time, big: !!big });
    if (this.ashMarks.length > 30) this.ashMarks.shift();
    const R = (a, b) => a + Math.random() * (b - a);
    const ASH = ['#6e6470', '#9a8f80', '#4a4450'];
    for (let i = 0; i < n; i++) {
      const a = R(0, TAU), s = R(20, big ? 150 : 90);
      const ember = Math.random() < 0.45;   // часть — всплывающие угли, часть — оседающий пепел
      this.particles.spawn((pt) => {
        pt.x = x + R(-4, 4); pt.y = y + R(-4, 4);
        // big (босс/игрок) — высокий плюмаж: угли всплывают выше, крупнее
        pt.vx = Math.cos(a) * s; pt.vy = Math.sin(a) * s - (ember ? R(big ? 25 : 10, big ? 60 : 35) : 0);
        pt.life = pt.maxLife = ember ? R(0.6, big ? 1.6 : 1.2) : R(1.0, big ? 2.6 : 2.0);
        pt.color = ember ? (Math.random() < 0.5 ? '#ff7a2a' : '#c9542a') : ASH[Math.floor(Math.random() * 3)];
        pt.size = ember ? R(1.5, big ? 4 : 3) : R(2, big ? 6 : 4);
        pt.fade = 1;
        pt.grav = ember ? R(big ? -45 : -30, big ? -20 : -12) : R(8, 20);   // угли всплывают, пепел оседает
        pt.damp = ember ? 1.1 : 0.7;                // дольше дрейфуют, чем неон-вспышка
      });
    }
  },

  // ВСПЫШКА РОСТА (§10.4 бит2 / §10.5 гиф): миг получения уровня — ты вспыхиваешь, и волна
  // жара ФИЗИЧЕСКИ отталкивает остывший рой наружу. «Ты растёшь — мир отшатывается». Это и есть
  // сигнатурный клипабельный момент позиционирования. Чистый knockback БЕЗ урона: кривая сложности
  // залочена (harness-страж), момент держится на отбросе+вспышке, не на бесплатном AoE-уроне.
  // Масштаб волны = текущий радиус жара (больше горишь → дальше отшатывается мир).
  levelUpFlash() {
    const p = this.player;
    const maxR = p.light * 1.05;
    const r2 = maxR * maxR;
    for (const e of this.enemies.active) {
      if (e.dead) continue;
      const d2 = dist2(e.x, e.y, p.x, p.y);
      if (d2 > r2) continue;
      const a = angleTo(p.x, p.y, e.x, e.y);
      const kbScale = clamp(30 / e.maxHp, 0.03, 1);          // тяжёлых сдвигает меньше — как в hitEnemy
      const falloff = 1 - Math.sqrt(d2) / maxR * 0.45;       // ближе к ядру — сильнее отбрасывает
      e.kx += Math.cos(a) * 440 * kbScale * falloff;
      e.ky += Math.sin(a) * 440 * kbScale * falloff;
      e.flash = CONFIG.feel.hitFlash;                        // мазок жара по краю — рой обожгло волной
    }
    // волна жара (кремово-белый — горячее=белее; голубой=холод, не сюда) + вспышка ядра + всплеск радиуса
    this.effects.push({ kind: 'nova', x: p.x, y: p.y, r: 0, maxR, life: 0.5, maxLife: 0.5, color: '#fff8d0' });
    p._coreFlash = 1;
    p.light = Math.min(CONFIG.light.max, p.light + 70);       // радиус вспыхивает (осядет к таргету)
    this.addShake(7);
    Audio2.nova();
  },

  // Световой пульс (пассивка): кольцо света отбрасывает и жжёт врагов вокруг игрока
  // ПРИМАНКА burst: срыв накопленного гашения — волна жара по толпе + частичный возврат света.
  lureBurst() {
    const p = this.player, LR = CONFIG.lure;
    const r = CONFIG.light.max * LR.burstRadiusMul, r2 = r * r;
    const dmg = LR.burstDmg * p.damageMult * CONFIG.balance.weaponDmg;
    for (const e of this.enemies.active) {
      if (e.dead) continue;
      if (dist2(e.x, e.y, p.x, p.y) <= r2) this.hitEnemy(e, dmg, LR.burstKb, p.x, p.y);
    }
    this.effects.push({ kind: 'nova', x: p.x, y: p.y, r: 0, maxR: r, life: 0.4, maxLife: 0.4, color: CONFIG.colors.player });
    this.burst(p.x, p.y, CONFIG.colors.player, 40, 320);
    p.light = Math.min(CONFIG.light.max, p.light + LR.burstLight);   // вспышка возвращает часть света
    this.addShake(10);
    Audio2.nova();
  },

  pulseWave() {
    const p = this.player;
    const r2 = p.pulseR * p.pulseR;
    for (const e of this.enemies.active) {
      if (e.dead) continue;
      if (dist2(e.x, e.y, p.x, p.y) <= r2) this.hitEnemy(e, p.pulseDmg, 320, p.x, p.y);
    }
    this.effects.push({ kind: 'nova', x: p.x, y: p.y, r: 0, maxR: p.pulseR, life: 0.34, maxLife: 0.34, color: '#fff0a0' });
    this.addShake(5);
    Audio2.nova();
  },

  spawnDmgNumber(x, y, value, crit) {
    this.dmgNumbers.spawn((d) => {
      d.x = x + rand(-6, 6); d.y = y;
      d.vy = -42; d.value = value; d.life = CONFIG.feel.dmgNumberLife;
      d.color = crit ? '#ffd24a' : '#ffffff'; d.crit = crit;
    });
  },

  addShake(mag) { this.shake = Math.min(30, Math.max(this.shake, mag)); },
  banner(text, time) { this.bannerText = text; this.bannerTime = time; this.bannerMax = time; },

  // ----------------------------- ЗАПРОСЫ -----------------------------
  nearestEnemy(x, y, maxD) {
    let best = null, bd = maxD * maxD;
    for (const e of this.enemies.active) {
      if (e.dead) continue;
      const d = dist2(x, y, e.x, e.y);
      if (d < bd) { bd = d; best = e; }
    }
    return best;
  },

  nearestEnemies(x, y, count, maxD) {
    const res: any[] = [];
    const md2 = maxD * maxD;
    for (const e of this.enemies.active) {
      if (e.dead) continue;
      const d = dist2(x, y, e.x, e.y);
      if (d > md2) continue;
      e._d = d;
      res.push(e);
    }
    res.sort((a, b) => a._d - b._d);
    return res.slice(0, count);
  },

  // ----------------------------- ДРАФТ / СОСТОЯНИЯ -----------------------------
  openDraft() {
    this.pendingLevels--;
    this.levelUpFlash();   // ВСПЫШКА РОСТА: волна жара отбрасывает рой ДО открытия драфта (момент роста)
    this.offers = this.generateOffers();
    this.selIndex = 0;
    this._draftIsEvent = false;
    this._draftIsAnomaly = false;
    this.state = 'levelup';
    this._draftOpenClock = this.clock;   // момент открытия — для вспышки левелапа
    this.dmgNumbers.clear();   // чтобы яркие числа не просвечивали сквозь дим
    Audio2.levelup();
  },

  // пересбор карт уровня — раз за забег. Только для обычного левелапа (не для
  // Завета/Развилки/Святилища, где «обновить» не имеет смысла — там выбор сам по себе).
  canReroll() {
    return this.state === 'levelup' && this.rerollsLeft > 0 && !this._draftIsAnomaly && !this._draftIsEvent;
  },
  rerollOffers() {
    if (!this.canReroll()) return;
    this.rerollsLeft--;
    this.offers = this.generateOffers();
    this.selIndex = 0;
    Audio2.uiMove();
  },

  // ---- Развилка Тьмы: выбор без правильного ответа (переиспользует UI драфта) ----
  openEvent() {
    this.eventsSpawned++;
    this._lastEventT = this.time;   // якорь для периодических развилок вечной ночи (S1b)
    this.offers = this.generateEventOffers();
    this.selIndex = 0;
    this._draftIsEvent = true;
    this._draftIsAnomaly = false;
    this.state = 'levelup';
    this._draftOpenClock = this.clock;
    this.dmgNumbers.clear();
    this.banner(t('banner.darkFork'), 1.6);
    Audio2.levelup();
  },

  generateEventOffers() {
    return sampleN(CONFIG.darkEvents, 3).map(d => ({ type: 'event', kind: d.kind, def: d }));
  },

  // Хэндлеры Развилок Тьмы — ТАБЛИЦА по kind (масштаб: новый вид = запись в CONFIG.darkEvents +
  // одна функция здесь, без роста if/else). Эффекты трогают состояние через this (enemies/runMods/
  // spawn) — намеренно код, не данные. Каждый — рекомбинация существующих примитивов.
  darkEventHandlers: {
    eliteWave(this: any) {
      const p = this.player;
      const ph = Spawner.currentPhase(this.time);
      const pool = ['tank', 'devourer', 'anchor', 'chaser'];
      for (let i = 0; i < 12; i++) {
        const pos = Spawner.edgePos();
        Spawner.spawnTypeAt(pick(pool), pos.x, pos.y, ph, false);
      }
      for (let i = 0; i < 14; i++)
        this.spawnPickup(p.x + rand(-60, 60), p.y + rand(-60, 60), 'gold', 5);
      this.banner(t('banner.eliteWave'), 1.8);
    },
    curse(this: any) {
      const p = this.player;
      // добавить аномалию, которой ещё нет; взамен — перманентный (на забег) +урон.
      // Баф даётся только если аномалия реально добавлена (иначе сделки нет)
      const have = new Set(this.runMods.anomalies.map(a => a.key));
      const fresh = CONFIG.anomalies.filter(a => !have.has(a.key));
      if (fresh.length) {
        const a = pick(fresh);
        this._mergeAnomaly(this.runMods, a);
        this.runMods.anomalies.push(a);
        this.banner(t('banner.anomaly', { name: tName('anomalies', a.key, a.name).toUpperCase() }), 2);
        p.base.damageMult *= 1.15; p.recalc();
      }
    },
    respite(this: any) {
      const p = this.player;
      p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.3);
      p.light = CONFIG.light.max;
      for (const e of this.enemies.active) {
        if (!e.dead && !e.isBoss && dist2(e.x, e.y, p.x, p.y) < 260 * 260) this.killEnemy(e);
      }
      this.burst(p.x, p.y, CONFIG.colors.player, 40, 280);
    },
    fortune(this: any) {
      const p = this.player;
      for (let i = 0; i < 22; i++)
        this.spawnPickup(p.x + rand(-80, 80), p.y + rand(-80, 80), 'gold', 5);
      p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.1);
    },
    // S1(c) ПРИЛИВ ЖАРА: окно временного глобального бафа урона + всплеск жара (переиспользует bless Источника)
    heattide(this: any) {
      const p = this.player;
      this.bless = { dmg: CONFIG.heattide.dmg, life: CONFIG.heattide.life, maxLife: CONFIG.heattide.life };
      p.light = Math.min(CONFIG.light.max, p.light + 120);
      this.burst(p.x, p.y, CONFIG.colors.boss, 36, 300);
      this.banner(t('banner.heattide'), 1.8);
    },
    // S1(c) РАСКОЛ: трещина роняет реликвию-тир бонус в забег (переиспользует applyRelic); всё взято — лечение
    rift(this: any) {
      const p = this.player;
      const pool = Object.keys(CONFIG.relics).filter((k: any) => !this.relicsOwned.includes(k));
      if (pool.length) this.applyRelic(pick(pool));
      else p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.2);
      // ЦЕНА (risk/reward — иначе бесплатная реликвия = no-brainer): трещина впускает холод
      const ph = Spawner.currentPhase(this.time);
      for (let i = 0; i < 6; i++) {
        const pos = Spawner.edgePos();
        Spawner.spawnTypeAt(pick(['tank', 'devourer', 'chaser']), pos.x, pos.y, ph, false);
      }
    },
  },
  applyDarkEvent(kind) {
    const h = this.darkEventHandlers[kind];
    if (h) h.call(this);
  },

  generateOffers() {
    const p = this.player;
    // доступные эволюции: оружие на ур.5 + нужная пассивка
    const evos: any[] = [];
    for (const key in CONFIG.weapons) {
      const def = CONFIG.weapons[key];
      const w = p.weapons.find(w => w.key === key);
      if (w && w.level >= 5 && def.evolveInto && (p.passives[def.evolveWith] || 0) >= 1) {
        evos.push({ type: 'evolve', key, into: def.evolveInto });
      }
    }
    // обычные кандидаты
    const cands: any[] = [];
    for (const key in CONFIG.weapons) {
      const def = CONFIG.weapons[key];
      if (p.weapons.some(x => x.key === def.evolveInto)) continue;  // уже эволюционировало
      const w = p.weapons.find(x => x.key === key);
      // запертое оружие не предлагаем, пока игрок его не открыл (если уже взято — качаем)
      if (!w && typeof Meta !== 'undefined' && !Meta.isUnlocked(key)) continue;
      if (!w) cands.push({ type: 'weapon', key, isNew: true, resLvl: 1 });
      else if (w.level < 5) cands.push({ type: 'weapon', key, isNew: false, resLvl: w.level + 1 });
    }
    for (const key in CONFIG.passives) {
      const lvl = p.passives[key] || 0;
      if (lvl < 5) cands.push({ type: 'passive', key, isNew: lvl === 0, resLvl: lvl + 1 });
    }
    // ГОРН-МОДЫ: «апгрейд апгрейда» — ветвят уже взятое оружие (prereq: оружие нужного уровня + узел дерева)
    for (const mk in CONFIG.forgeMods) {
      const fm = CONFIG.forgeMods[mk];
      const w = p.weapons.find(x => x.key === fm.weapon);
      if (!w) continue;                                   // оружие не взято
      if (w[fm.flag]) continue;                           // мод уже стоит
      if (fm.req && w.level < fm.req.level) continue;     // не дорос до уровня
      if (fm.need && !w[fm.need]) continue;               // нет предыдущего узла дерева
      if (fm.excl && w[fm.excl]) continue;                // выбрана взаимоисключающая ветка
      cands.push({ type: 'mod', key: mk, weaponKey: fm.weapon, isNew: true });
    }
    // ОВЕРКЛОК (горн-класс B): макснутое оружие (ур.5 или эволюция) можно перегреть ценой света
    for (const w of p.weapons) {
      const maxed = w.level >= 5 || !!CONFIG.evolutions[w.key];
      if (maxed && (w.oc || 0) < CONFIG.overclock.maxStacks) cands.push({ type: 'overclock', key: w.key });
    }
    // гарантированно показать одну эволюцию, если доступна
    let chosen: any[] = [];
    if (evos.length) chosen.push(pick(evos));
    chosen = chosen.concat(sampleN(cands, 3 - chosen.length));
    while (chosen.length < 3) chosen.push({ type: 'heal' });
    return chosen;
  },

  chooseOffer(i) {
    const o = this.offers[i];
    if (!o) return;
    const p = this.player;
    if (o.type === 'anomaly') {
      // «Завет тьмы» на старте: влить выбранную аномалию и предложить следующую (или войти в забег)
      this._mergeAnomaly(this.runMods, o.def);
      this.runMods.anomalies.push(o.def);
      this._anomalyPicksLeft--;
      Audio2.uiPick();
      this.offers = [];
      this.openAnomalyDraft();   // сам решит: ещё выбор или beginPlay()
      return;
    }
    if (o.type === 'weapon') {
      const before = new Set(p._synergies || []);
      p.addWeapon(o.key);   // recalc внутри обновит _synergies
      const fresh = (p._synergies || []).find((k) => !before.has(k));   // новая активная синергия → момент
      if (fresh) {
        const sd = CONFIG.synergies.find((s) => s.key === fresh);
        if (sd) { this.banner(t('banner.synergy', { name: tName('synergies', fresh, sd.name) }), 2.4); this.addShake(7); Audio2.levelup(); }
      }
    }
    else if (o.type === 'passive') p.addPassive(o.key);
    else if (o.type === 'mod') {
      // ГОРН-МОД: «апгрейд апгрейда» — ставит флаг на объект оружия, боевой код читает его in-situ
      const fm = CONFIG.forgeMods[o.key];
      const w = fm && p.weapons.find(x => x.key === fm.weapon);
      if (w) {
        w[fm.flag] = true;
        this.banner(t('banner.forge', { name: tName('forgeMods', o.key, fm.name) }), 2.2);
        this.addShake(6); Audio2.levelup();
      }
    }
    else if (o.type === 'overclock') {
      // ОВЕРКЛОК: +стак макснутому оружию (урон/кулдаун ↑, свет ↓ — дрейф к тёмному полюсу, см. updatePlayer)
      const w = p.weapons.find(x => x.key === o.key);
      if (w) {
        w.oc = (w.oc || 0) + 1;
        const nm = CONFIG.evolutions[w.key] ? tName('evolutions', w.key, CONFIG.evolutions[w.key].name)
                                            : tName('weapons', w.key, CONFIG.weapons[w.key].name);
        this.banner(t('banner.overclock', { name: nm }), 2.2);
        this.addShake(7); Audio2.levelup();
      }
    }
    else if (o.type === 'heal') p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.25);
    else if (o.type === 'relic') this.applyRelic(o.key);
    else if (o.type === 'event') this.applyDarkEvent(o.kind);
    else if (o.type === 'evolve') {
      const w = p.weapons.find(w => w.key === o.key);
      if (w) {
        // эволюция = ДРУГОЕ оружие → сбросить горн-моды базы (флаги привязаны к старому ключу) + оверклок
        for (const mk in CONFIG.forgeMods) delete w[CONFIG.forgeMods[mk].flag];
        w.oc = 0;
        w.key = o.into; w.level = 1; w.timer = 0; w.orbAngle = 0; w._nodes = []; w._beamTick = 0; w._beams = null;
      }
      p.recalc();   // ключ оружия сменился (эволюция) → пересчитать синергии
      this.banner(t('banner.evolution', { name: tName('evolutions', o.into, CONFIG.evolutions[o.into].name) }), 2.4);
      this.addShake(8);
      Audio2.levelup();
    }
    else if (o.type === 'boon') this.beginSnare(o.key);   // TD-захват: выбрана опора → начать удержание
    Audio2.uiPick();
    this.offers = [];
    this._draftIsShrine = false; this._draftIsSnare = false;
    if (this.pendingLevels > 0) this.openDraft();
    else this.state = 'playing';
  },

  togglePause() {
    if (this.state === 'playing') { this.state = 'paused'; this.dmgNumbers.clear(); }
    else if (this.state === 'paused') this.state = 'playing';
  },

  openCharSelect() {
    this.state = 'charselect';
    this.charIndex = Object.keys(CONFIG.characters).indexOf(this.selectedChar);
    if (this.charIndex < 0) this.charIndex = 0;
    Audio2.ensure(); Audio2.resume();
  },
  confirmChar() {
    const keys = Object.keys(CONFIG.characters);
    const key = keys[clamp(this.charIndex, 0, keys.length - 1)];
    if (typeof Meta !== 'undefined' && !Meta.isUnlocked(key)) { Audio2.hit(); return; }  // герой заперт
    const pdef = CONFIG.paths[this.selectedPath || 0];
    if (typeof Meta !== 'undefined' && pdef && !Meta.isUnlocked(pdef.key)) { Audio2.hit(); return; }  // Путь заперт
    this.selectedChar = key;
    this.start();
  },
  openShop() {
    this.state = 'shop';
    this.shopIndex = 0;
    Audio2.ensure(); Audio2.resume();
  },
  quitToMenu() {
    this.awardRun();            // забранное золото засчитываем
    this.state = 'menu';
    Audio2.stopMusic();
  },
  openSettings(from: string) {
    this._settingsFrom = from;
    this.state = 'settings';
    document.getElementById('game-pixi')?.classList.add('settings-blur');
  },
  closeSettings() {
    this.state = this._settingsFrom || 'menu';
    document.getElementById('game-pixi')?.classList.remove('settings-blur');
  },

  // золото за забег + рекорд + вехи разблокировок (раз за забег)
  awardRun() {
    if (this._runAwarded) return;
    this._runAwarded = true;
    const rew = this.runMods ? this.runMods.reward : 1;
    const base = this.player.gold + Math.floor(this.player.kills / 5) + Math.floor(this.score / 4);
    const earned = Math.round(base * rew);
    this.runGold = earned;
    this.newUnlocks = [];
    if (typeof Meta !== 'undefined') {
      Meta.data.gold += earned;
      this.newUnlocks = Meta.recordRun({
        time: this.time, level: this.player.level, kills: this.player.kills,
        killedBoss: this.killedBoss, won: this.state === 'win' || this.reachedDawn,
        depth: this.depthIndex, seen: this.runSeen,
        path: (CONFIG.paths[this.selectedPath || 0] || {}).key,   // какой Путь сыгран (для вех «открой Путём X»)
      });
      this.menuVoice = this._menuFragment();   // фрагмент «Пепелища» на меню по итогу забега (§14.4)
    }
  },

  gameOver() {
    const p = this.player;
    // второе дыхание (перм-апгрейд): восстать вместо гибели
    if (p._revives > 0) {
      p._revives--;
      p.hp = p.maxHp * 0.6;
      p.invuln = 2.5;
      p.light = CONFIG.light.max;
      this.banner(t('banner.revive'), 2);
      // развеять ближнюю тьму
      for (const e of this.enemies.active) {
        if (!e.dead && !e.isBoss && dist2(e.x, e.y, p.x, p.y) < 320 * 320) {
          this.killEnemy(e);
        }
      }
      this.burst(p.x, p.y, CONFIG.colors.player, 60, 360);
      this.addShake(18);
      Audio2.levelup();
      return;
    }
    this.state = 'gameover';
    this.deathVoice = this._pickLore('death');   // голос Пепелища на экране смерти (§14.1)
    this.addShake(14);
    this.ashBurst(p.x, p.y, 64, true);   // игрок ЕСТЬ пламя: его смерть — рассыпание в пепел, не неон-вспышка (главный момент забега)
    this.awardRun();
    Audio2.stopMusic();
    Audio2.death();
  },

  // Рассвет: веха на 10:00. Засчитывает мету (открытие глубины, win-разблокировки,
  // рекорд) единоразово, даёт право «уйти победой» — но забег продолжается в вечной
  // ночи (эскалация и боссы идут бесконечно). Не прерывает игру.
  reachDawn() {
    this.reachedDawn = true;
    const p = this.player;
    p.light = CONFIG.light.max;
    p._lightFloor = CONFIG.light.max; p._lightFloorT = 3;
    this.banner(t('banner.dawn'), 3.2);
    this.addShake(16);
    this.burst(p.x, p.y, CONFIG.colors.player, 70, 360);
    Audio2.bossDie();
    if (Audio2.music) Audio2.musicSetMood('dawn');
  },

  // добровольно завершить забег победой (доступно только после Рассвета — из паузы).
  // Единственный путь в state 'win' (бывший win()-по-таймеру убран ради бесконечности).
  finishVictory() {
    if (!this.reachedDawn) return;
    this.state = 'win';
    this.winVoice = this._pickLore('victory');   // голос Пепелища на победе (§14.4)
    this.awardRun();
    Audio2.stopMusic();
    Audio2.levelup();
  },
};
